import {
  InventoryListing,
  InventoryEmailItem,
  useListAuditLog,
  getListAuditLogQueryKey,
  useListInventoryEmails,
  getListInventoryEmailsQueryKey,
  useGetSaleEmail,
  getGetSaleEmailQueryKey,
  useGetInventoryReachNote,
  getGetInventoryReachNoteQueryKey,
  useGetMe,
  useListInventoryLinks,
  useSetBuybackGetIn,
  useListBuybackCandidates,
  useGetBuybackMatches,
  useMarkBuybackFailed,
  useConfirmBuybackLink,
  useFailBuybackLink,
  useAddBuybackLink,
  useFlagNeedHelp,
  useOfferAssistance,
  type BuybackCandidateList,
  type BuybackMatchList,
} from "@workspace/api-client-react";
import { SalePickerModal } from "@/components/sale-picker-modal";
import { linkCoverageStats } from "@/lib/link-coverage";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Textarea } from "@/components/ui/textarea";
import type { FveTransitions } from "@/lib/use-fve-transitions";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActivityTimeline } from "@/components/activity-feed";
import { EmailViewerModal } from "@/components/email-viewer-modal";
import {
  Hand,
  Loader2,
  ExternalLink,
  StickyNote,
  Pencil,
  BellRing,
  Check,
  History,
  Mail,
  Ban,
  RotateCcw,
  Tag,
  ChevronDown,
  ShoppingBag,
  Percent,
  Store,
  Radio,
  QrCode,
  AlertTriangle,
  Trash2,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Link2,
  TrendingUp,
  CheckCircle2,
  Users,
  UserCheck,
  Minus,
  HandCoins,
  X,
} from "lucide-react";
import { CopyButton } from "@/components/bank-numbers";
import { cn } from "@/lib/utils";

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  CAD: "C$",
  AUD: "A$",
};

export function currencySymbol(code?: string | null): string {
  if (!code) return "$";
  return CURRENCY_SYMBOLS[code.toUpperCase()] ?? `${code.toUpperCase()} `;
}

// Short label for why a buyback candidate was excluded, shown in the picker's
// debug detail so the operator understands the ranking.
export function excludeReasonLabel(reason: string | null | undefined): string {
  switch (reason) {
    case "bound_out":
      return "bound out";
    case "no_verification":
      return "unverified";
    case "stale_verification":
      return "stale verify";
    case "no_good_phone":
      return "no phone";
    default:
      return "excluded";
  }
}

// Compact "email last verified" date for the candidate rows.
export function formatVerified(iso: string | null | undefined): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatMoney(
  amount?: number | null,
  code?: string | null,
): string {
  if (amount == null) return "-";
  return `${currencySymbol(code)}${amount.toFixed(2)}`;
}

export type HoldState = "none" | "held" | "expiring" | "expired" | "failed";

export function holdState(row: InventoryListing): HoldState {
  // A read-back that didn't match the requested state is a failed verification,
  // surfaced regardless of the active/expiration state so operators can re-run it.
  if (row.adminHoldVerified === false) return "failed";
  if (!row.adminHoldActive) return "none";
  if (!row.adminHoldExpiration) return "held";
  const exp = new Date(row.adminHoldExpiration).getTime();
  const now = Date.now();
  if (Number.isNaN(exp)) return "held";
  if (exp <= now) return "expired";
  if (exp - now <= 24 * 60 * 60 * 1000) return "expiring";
  return "held";
}

// Sort rank so the Hold column orders by urgency rather than alphabetically.
export const HOLD_RANK: Record<HoldState, number> = {
  none: 0,
  held: 1,
  expiring: 2,
  expired: 3,
  failed: 4,
};

export function formatHoldDate(s?: string | null): string | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatHoldDateCompact(s?: string | null): string | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Numeric-aware natural compare so "Sec 2" sorts before "Sec 10" and seats
// order numerically rather than lexically.
export const nat = (a?: string | null, b?: string | null) =>
  (a ?? "").localeCompare(b ?? "", undefined, {
    numeric: true,
    sensitivity: "base",
  });

export function naturalSeatCompare(a: InventoryListing, b: InventoryListing) {
  return nat(a.section, b.section) || nat(a.row, b.row) || nat(a.seatFrom, b.seatFrom);
}

// ─── Account Info Popover ─────────────────────────────────────────────────────

function PhoneStatusBadge({ status }: { status: string | null | undefined }) {
  const s = status ?? "unknown";
  const cls =
    s === "good"
      ? "bg-green-500/15 text-green-400 border-green-500/40"
      : s === "bad"
        ? "bg-red-500/15 text-red-400 border-red-500/40"
        : "bg-muted text-muted-foreground border-border";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${cls}`}
    >
      {s}
    </span>
  );
}

function MaskedField({
  value,
  label,
}: {
  value: string;
  label: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-xs text-foreground tabular-nums truncate">
          {value}
        </span>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
  copyValue?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0 justify-end">{value}</div>
    </div>
  );
}

function InfoRowCopy({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="font-mono text-xs text-foreground truncate" title={value}>
          {value}
        </span>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

interface AccountInfoPopoverProps {
  vendorAccountEmail?: string | null;
  tmPassword?: string | null;
  phoneOnFile?: string | null;
  phoneStatus?: string | null;
  pullerName?: string | null;
}

export function AccountInfoPopover({
  vendorAccountEmail,
  tmPassword,
  phoneOnFile,
  phoneStatus,
  pullerName,
}: AccountInfoPopoverProps) {
  const email = vendorAccountEmail ?? null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 min-w-0 text-left hover:underline decoration-dotted underline-offset-2 text-xs whitespace-nowrap"
          title={email ?? undefined}
        >
          <span className="truncate">{email || "-"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 space-y-2">
        <div className="text-xs font-medium text-foreground truncate" title={email ?? undefined}>
          {email || "—"}
        </div>
        <div className="space-y-1.5 border-t border-border pt-2">
          {email && <InfoRowCopy label="Email" value={email} />}
          {tmPassword ? (
            <MaskedField label="Password" value={tmPassword} />
          ) : (
            <InfoRow label="Password" value={<span className="text-xs text-muted-foreground/60">—</span>} />
          )}
          {phoneOnFile ? (
            <InfoRowCopy label="Phone" value={phoneOnFile} />
          ) : (
            <InfoRow label="Phone" value={<span className="text-xs text-muted-foreground/60">—</span>} />
          )}
          <InfoRow
            label="Phone Status"
            value={<PhoneStatusBadge status={phoneStatus} />}
          />
          {pullerName ? (
            <InfoRow
              label="Puller"
              value={
                <span className="text-xs text-foreground truncate" title={pullerName}>
                  {pullerName}
                </span>
              }
            />
          ) : (
            <InfoRow label="Puller" value={<span className="text-xs text-muted-foreground/60">—</span>} />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// A short relative countdown for an admin hold ("in 3d", "in 5h", "expired 2h
// ago"). Used to surface hold timing on the FVE pill now that the dedicated
// Hold / Hold Exp columns are gone.
export function formatHoldCountdown(s?: string | null): string | null {
  if (!s) return null;
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return null;
  const diff = t - Date.now();
  const abs = Math.abs(diff);
  const day = 86_400_000;
  const hour = 3_600_000;
  const min = 60_000;
  const mag =
    abs >= day
      ? `${Math.round(abs / day)}d`
      : abs >= hour
        ? `${Math.round(abs / hour)}h`
        : `${Math.max(1, Math.round(abs / min))}m`;
  return diff < 0 ? `expired ${mag} ago` : `in ${mag}`;
}

// Where the single context-aware Price column gets its displayed value from.
export type EffectivePriceSource =
  | "email"
  | "operator"
  | "recommended"
  | "face"
  | "none";

// Resolve the one price worth showing per listing, preferring the confirmed
// list price (from a listing email or an operator's decision), then an
// admin-set recommended price, then the true face value. `recommendedListPrice`
// is never null server-side — it defaults to face value while
// `recommendedListPriceSetBy` is null — so an unset recommendation collapses to
// the face-value rung rather than masking it.
export function effectivePrice(row: InventoryListing): {
  value: number | null;
  source: EffectivePriceSource;
} {
  if (row.listedPrice != null) {
    return {
      value: row.listedPrice,
      source: row.listedSource === "email" ? "email" : "operator",
    };
  }
  if (row.recommendedListPriceSetBy != null && row.recommendedListPrice != null) {
    return { value: row.recommendedListPrice, source: "recommended" };
  }
  if (row.faceValue != null) {
    return { value: row.faceValue, source: "face" };
  }
  return { value: null, source: "none" };
}

// Percent the effective price sits below (negative) or above (positive) face
// value. Returns 0 when there's no face to compare against or the effective
// price IS the face value.
export function priceVsFace(row: InventoryListing): number {
  const eff = effectivePrice(row);
  const face = row.faceValue;
  if (eff.value == null || face == null || face <= 0 || eff.source === "face") {
    return 0;
  }
  return Math.round(((eff.value - face) / face) * 100);
}

// Compact "Section / Row / Seats" summary for the merged seats column. Seats use
// the server-provided display string when present, else a from-to range, and
// missing parts are dropped so partial data still reads cleanly.
export function seatSummary(row: InventoryListing): string {
  const seats = row.seatsDisplay?.trim()
    ? row.seatsDisplay.trim()
    : row.seatFrom
      ? row.seatTo && row.seatTo !== row.seatFrom
        ? `${row.seatFrom}-${row.seatTo}`
        : `${row.seatFrom}`
      : "";
  const parts = [row.section, row.row, seats]
    .map((p) => (p != null && String(p).trim() ? String(p).trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}

// A row counts as "bought back" (and is hidden from the main view unless the
// operator toggles "show bought-back") when any of:
//   1. the server auto-classified it as a buy-back re-purchase — either a folded
//      duplicate/subset child or a last-minute purchase inside the 72h window
//      before a buy-back event (buybackRepurchase);
//   2. it was explicitly folded into an original listing (buybackChildOf);
//   3. Reach surfaced a confirmed, non-ambiguous buyback purchase order for it —
//      the green DETECTED state (ambiguous matches are excluded because they
//      still need the operator to pick the right purchase; mirrors the server's
//      buybackMatchScope === "seat_group").
export function isBoughtBack(row: InventoryListing): boolean {
  return (
    row.buybackRepurchase === true ||
    row.buybackChildOf != null ||
    (row.buybackBbPurchaseOrderId != null && !row.buybackMatchAmbiguous)
  );
}

// Bordered chip that displays a pre-formatted "SEC / ROW / SEATS" string.
// Renders nothing when the seat string is empty or the placeholder dash.
export function SeatPill({
  seat,
  className,
}: {
  seat: string;
  className?: string;
}) {
  if (!seat || seat === "-") return null;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border border-border/60 bg-muted/50 px-1.5 py-0.5 font-mono text-[10px] leading-none text-foreground whitespace-nowrap",
        className,
      )}
    >
      {seat}
    </span>
  );
}

// Quantity shown in the Qty column. A buy-back candidate is sold-but-unfulfilled
// (its availableQuantity is 0), so the actionable count is its ticket count;
// ordinary remaining inventory uses the available quantity. Used by every view
// that lists inventory rows so the Qty column (and its totals) always agree.
export function displayQty(row: {
  buybackCandidate?: boolean | null;
  ticketCount?: number | null;
  availableQuantity?: number | null;
}): number {
  return row.buybackCandidate
    ? (row.ticketCount ?? 0)
    : (row.availableQuantity ?? 0);
}

type FveStatus =
  | "eligible"
  | "held"
  | "listed"
  | "listed_discount"
  | "via_reach"
  | "via_skybox"
  | "cannot_list"
  | "need_help"
  | "buy_back_on_fve"
  | "sell_on_fve"
  | "surrender_on_fve"
  | "sold"
  | "sold_partial"
  | "sale_cancelled";

// The move-to-marketplace endpoint returns a 409 with this error code when the
// row already has a confirmed sale. The generated client throws an ApiError that
// carries the HTTP status and parsed JSON body, so we duck-type both here.
function isConfirmDoubleSale(err: unknown): boolean {
  if (typeof err !== "object" || err == null) return false;
  const status = (err as { status?: number }).status;
  const code = (err as { data?: { error?: string } }).data?.error;
  return status === 409 && code === "CONFIRM_DOUBLE_SALE";
}

// Visual treatment per derived FVE status. Held additionally takes accent
// colours from holdSubState (expiring/expired/failed) so urgency reads at a
// glance without leaving the Held state.
function pillMeta(row: InventoryListing): {
  label: string;
  className: string;
  verified?: boolean;
  discounted?: boolean;
} {
  const status = (row.fveStatus ?? "eligible") as FveStatus;
  // An email-confirmed listing reads as verified (checkmark + stronger colour);
  // an operator-set listing is muted with no checkmark.
  const emailVerified = row.listedSource === "email";
  switch (status) {
    case "sold":
      return {
        label: "Sold",
        className: "bg-purple-500/15 text-purple-300 border-purple-500/40",
      };
    case "sold_partial":
      return {
        label: "Sold Partial",
        className: "bg-orange-500/15 text-orange-300 border-orange-500/40",
      };
    case "sale_cancelled":
      return {
        label: "Sale Cancelled",
        className: "bg-amber-500/15 text-amber-300 border-amber-500/40",
      };
    case "cannot_list":
      return {
        label: "Cannot List",
        className: "bg-red-500/15 text-red-300 border-red-500/40",
      };
    case "via_reach":
      return {
        label: "Selling via Reach",
        className: "bg-violet-500/15 text-violet-300 border-violet-500/40",
      };
    case "via_skybox":
      return {
        label: "Via Skybox",
        className: "bg-violet-500/15 text-violet-300 border-violet-500/40",
      };
    case "listed_discount":
      return emailVerified
        ? {
            label: "Listed",
            className: "bg-amber-500/25 text-amber-200 border-amber-500/60",
            verified: true,
            discounted: true,
          }
        : {
            label: "Listed",
            className: "bg-muted text-muted-foreground border-border",
            discounted: true,
          };
    case "listed":
      return emailVerified
        ? {
            label: "Listed",
            className: "bg-emerald-500/25 text-emerald-200 border-emerald-500/60",
            verified: true,
          }
        : {
            label: "Listed",
            className: "bg-muted text-muted-foreground border-border",
          };
    case "need_help":
      return {
        label: "Need Help",
        className: "bg-orange-500/20 text-orange-300 border-orange-500/50",
      };
    case "buy_back_on_fve":
      return {
        label: "Buy Back",
        className: "bg-amber-500/15 text-amber-300 border-amber-500/40",
      };
    case "sell_on_fve":
      return {
        label: "Sell",
        className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      };
    case "surrender_on_fve":
      return {
        label: "Surrender",
        className: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
      };
    default:
      return {
        label: "Eligible",
        className: "bg-muted text-muted-foreground border-border",
      };
  }
}

// Plain-English explanation of the badge's current meaning, driven by the same
// status / verified (listedSource) / discounted / hold-sub-state signals the
// badge already renders. Surfaced on hover so operators don't have to memorise
// the colour/icon code. The less obvious states get a fuller sentence.
export function pillTooltip(row: InventoryListing): { title: string; lines: string[] } {
  const status = (row.fveStatus ?? "eligible") as FveStatus;
  const emailVerified = row.listedSource === "email";
  switch (status) {
    case "sold":
      return {
        title: "Sold",
        lines: ["Confirmed sold by a matched Reach sale email."],
      };
    case "sold_partial":
      return {
        title: "Sold Partial",
        lines: [
          "Only some of this listing's confirmed tickets have sold — the rest are still available. Use 'Sell in Reach' to push the remaining inventory back to a marketplace.",
        ],
      };
    case "sale_cancelled":
      return {
        title: "Sale Cancelled",
        lines: [
          "A previously confirmed sale was cancelled by Ticketmaster. The listing is available again — re-list it from the actions menu.",
        ],
      };
    case "cannot_list":
      return {
        title: "Cannot List",
        lines: [
          "An operator flagged this listing as one that can't go on the Fair Value Exchange (e.g. a bad phone or banking issue). Re-enable it from the actions menu once it's resolved.",
        ],
      };
    case "via_reach":
      return {
        title: "Selling via Reach",
        lines: [
          "Selling on Reach, an external marketplace outside the Fair Value Exchange. It's been taken off the FVE so it can't sell in two places at once.",
        ],
      };
    case "via_skybox":
      return {
        title: "Via Skybox",
        lines: [
          "Being sold on Skybox (VividSeats), an external marketplace outside the Fair Value Exchange. It's been taken off the FVE so it can't sell in two places at once.",
        ],
      };
    case "need_help": {
      const note = row.needHelpNote;
      return {
        title: "Need Help",
        lines: [
          "This puller needs help fulfilling this buyback. It is broadcast to all pullers until someone claims it.",
          ...(note ? [`Note: ${note}`] : []),
        ],
      };
    }
    case "buy_back_on_fve":
      return {
        title: "Buy Back on FVE",
        lines: [
          "Operator has marked this listing as intended for buyback on the FVE. The margin / get-in column is hidden; use the intent picker to change or clear this.",
        ],
      };
    case "sell_on_fve":
      return {
        title: "Sell on FVE",
        lines: [
          "Operator has marked this listing as intended to sell on the FVE. The margin / get-in column is hidden; use the intent picker to change or clear this.",
        ],
      };
    case "surrender_on_fve":
      return {
        title: "Surrender",
        lines: [
          "This listing has been surrendered — the tickets cannot be sold, bought back, or listed. Re-enable it from the actions menu to return it to normal.",
        ],
      };
    case "listed":
    case "listed_discount": {
      const lines = ["Listed on the Fair Value Exchange."];
      lines.push(
        emailVerified
          ? "Confirmed by a Reach email — shown in a bright colour with a checkmark."
          : "Marked by an operator, not yet confirmed by a Reach email — shown in muted black.",
      );
      if (status === "listed_discount") {
        lines.push("The % icon means it's listed below face value.");
      }
      return { title: "Listed", lines };
    }
    default:
      return {
        title: "Eligible",
        lines: [
          "Ready to be held and listed on the Fair Value Exchange. Click to place a hold or mark it listed.",
        ],
      };
  }
}

// A small warning triangle shown next to the FVE status badge whenever a row
// has open double-sell / double-exposure issues. Its colour reflects the worst
// severity (yellow = warning, orange = high, red = critical). Hovering lists
// every issue in plain English; SkyBox-sourced issues offer a click-through to
// the SkyBox page for users with SkyBox access (admin or pricing).
function IssueWarning({ row }: { row: InventoryListing }) {
  const { data: me } = useGetMe();
  const issues = row.issues ?? [];
  if (issues.length === 0) return null;

  const worst = row.worstIssueSeverity ?? "warning";
  const colorClass =
    worst === "critical"
      ? "text-red-500"
      : worst === "high"
        ? "text-orange-400"
        : "text-yellow-400";

  const canSkybox = me?.role === "admin" || me?.role === "pricing";
  // Open the external SkyBox app directly (new tab) rather than navigating to
  // the in-app SkyBox page. Prefer the SkyBox invoice deep-link for a
  // sold-in-SkyBox exposure, falling back to any SkyBox link the API resolved.
  const skyboxIssues = issues.filter((i) => i.source === "skybox" && i.url);
  const skyboxUrl = canSkybox
    ? (skyboxIssues.find((i) => i.url?.includes("/invoices/"))?.url ??
      skyboxIssues[0]?.url ??
      null)
    : null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn("inline-flex items-center", colorClass)}
          aria-label={`${issues.length} exposure ${
            issues.length === 1 ? "warning" : "warnings"
          }`}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[280px]">
        <div className="space-y-1.5">
          <div className="text-xs font-semibold">
            {issues.length === 1
              ? "Exposure warning"
              : `${issues.length} exposure warnings`}
          </div>
          <ul className="space-y-1">
            {issues.map((issue, i) => (
              <li
                key={i}
                className="text-[11px] leading-snug flex items-start gap-1"
              >
                <AlertTriangle
                  className={cn(
                    "mt-0.5 h-3 w-3 shrink-0",
                    issue.severity === "critical"
                      ? "text-red-500"
                      : issue.severity === "high"
                        ? "text-orange-400"
                        : "text-yellow-400",
                  )}
                />
                <span>{issue.label}</span>
              </li>
            ))}
          </ul>
          {skyboxUrl && (
            <a
              href={skyboxUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-primary hover:underline cursor-pointer inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> Open in SkyBox
            </a>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// One-click derived FVE status pill (Option A). The pill colour reflects the
// derived state; clicking opens a popover of the valid next actions for that
// state. Email-driven provenance is flagged with a mail glyph that links to the
// triggering email. Mutations are supplied via the shared useFveTransitions
// hook so invalidation matches the parent table.
export function FveStatusPill({
  row,
  fve,
  hideGetIn = false,
}: {
  row: InventoryListing;
  fve: FveTransitions;
  hideGetIn?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  // Which secondary action's inline mini-form is showing, if any. `null` is the
  // primary view (price + secondary tiles).
  const [secondaryView, setSecondaryView] =
    useState<null | "cannot_list" | "surrender" | "move_reach" | "flag_help">(null);
  // True from the moment a transition is fired until it settles, so the popup
  // can show an in-flight state and then close itself when the action lands.
  const [submitting, setSubmitting] = useState(false);
  // Set when the server rejects a move on an already-sold row with a
  // CONFIRM_DOUBLE_SALE 409. Flips the move_reach view to its double-sale
  // warning so the operator must explicitly confirm before we resend with force.
  const [doubleSaleConfirm, setDoubleSaleConfirm] = useState(false);
  // When set, the in-place email viewer modal is open for this email id. Opening
  // it closes the popover/sheet so the two overlays never stack.
  const [emailModalId, setEmailModalId] = useState<number | null>(null);
  // Buyback account picker state: which pre-loaded POOL account the operator has
  // manually picked. This is the operator's manual selection
  // (inventory.buybackSelectedAccountId -> buyback_accounts.id) and is entirely
  // separate from the auto-DETECTED re-purchase account (row.buybackAccountId ->
  // accounts.id), which drives the out-of-scope completion flow below.
  const [selectedPoolAccountId, setSelectedPoolAccountId] = useState<
    number | null
  >(row.buybackSelectedAccountId ?? null);
  // Hide pool accounts already applied to another purchase by default so the
  // operator is steered to a fresh account; the currently-selected one is never
  // hidden even when used.
  const [excludeUsed, setExcludeUsed] = useState(true);
  const queryClient = useQueryClient();
  const [buybackCompleting, setBuybackCompleting] = useState(false);
  // buybackCompleted is derived from the serialized row.buybackCompletedAt so
  // the lock persists even if the popover is reopened.  We also allow a local
  // optimistic flag so the UI disables immediately on success (before the row
  // data refreshes from the server).
  const [buybackCompletedLocal, setBuybackCompletedLocal] = useState(false);
  const buybackCompleted = buybackCompletedLocal || !!row.buybackCompletedAt;
  // Note text for the "Flag for Help" mini-form.
  const [helpNote, setHelpNote] = useState("");
  // On phones the fixed-width popover is cramped; render the same content in a
  // bottom sheet instead so the tile grid and mini-forms have full width.
  const isMobile = useIsMobile();

  const { data: me } = useGetMe();

  const status = (row.fveStatus ?? "eligible") as FveStatus;
  const meta = pillMeta(row);
  const tip = pillTooltip(row);
  const sym = currencySymbol(row.currencyCode);

  // Listing requires an active hold (held/expiring) unless the row is already
  // listed (then the action is just editing the price).
  const hs = holdState(row);
  const holdActive = hs === "held" || hs === "expiring";
  const isListed = status === "listed" || status === "listed_discount";
  const canList = holdActive || isListed;
  // Already-sold rows (fully or partially sold) can also be pushed back to Reach,
  // but the move is gated behind a double-sale confirmation (server 409).
  const isSold = status === "sold" || status === "sold_partial";
  const movable =
    status === "held" ||
    status === "eligible" ||
    status === "cannot_list" ||
    isListed ||
    isSold;

  // In buyback-display mode (settings.hideGetIn) the Zone-3 marketplace tiles
  // are replaced with an explicit FVE intent picker (Buy Back / Sell) on EVERY
  // listing of a buy-back event. Event-level buybacksEnabled is the only gate:
  // the operator can set intent regardless of whether the specific listing has
  // sold yet (i.e. it need not be a sold-but-unfulfilled buybackCandidate). The
  // server intent / candidates / account routes likewise gate only on
  // event-level buybacksEnabled, so a non-candidate row is accepted.
  const buybackEligible = !!row.buybacksEnabled;
  const showIntentTiles = hideGetIn && buybackEligible;
  const currentIntent = (row.buybackFveIntent ?? null) as BuybackFveIntent;
  const showAccountPicker = showIntentTiles && currentIntent === "buy_back";

  // Fetch ranked candidates when the picker section is visible.
  const candidatesQuery = useListBuybackCandidates(row.id, {
    query: {
      enabled: open && showAccountPicker,
      queryKey: [`/api/inventory/${row.id}/buyback-candidates`, open],
    },
  });
  const candidates = (candidatesQuery.data as BuybackCandidateList | undefined)
    ?.accounts ?? [];
  // The ranking endpoint returns every account with eligibility flags. Only
  // eligible accounts are selectable; the rest are shown in a collapsed debug
  // list so the operator can see why each was excluded. When "hide used" is on
  // we drop pool accounts already applied to another purchase, but always keep
  // the currently-selected one so the operator's own pick never disappears.
  const isVisible = (c: BuybackCandidateList["accounts"][number]) =>
    !excludeUsed || !c.used || c.id === selectedPoolAccountId;
  const eligibleCandidates = candidates.filter(
    (c) => c.eligible && isVisible(c),
  );
  const excludedCandidates = candidates.filter(
    (c) => !c.eligible && isVisible(c),
  );
  // The top-ranked eligible account (before any used-filtering) is flagged
  // "Recommended" so the operator has a steer without any auto-assignment.
  const recommendedId = candidates.find((c) => c.eligible)?.id ?? null;
  // How many eligible accounts are hidden purely because they're already used —
  // drives the "show used" affordance so nothing silently vanishes.
  const hiddenUsedCount = candidates.filter(
    (c) => c.eligible && c.used && c.id !== selectedPoolAccountId,
  ).length;

  // When the detection found multiple confident matches the row is marked
  // ambiguous and never auto-linked — load the matches so the operator can pick.
  const ambiguous = !!row.buybackMatchAmbiguous;
  const matchesQuery = useGetBuybackMatches(row.id, {
    query: {
      enabled: open && showAccountPicker && ambiguous,
      queryKey: [`/api/inventory/${row.id}/buyback-matches`, open],
    },
  });
  const matches =
    (matchesQuery.data as BuybackMatchList | undefined)?.matches ?? [];

  // A transition is in flight for this specific row.
  const busy = fve.busyId === row.id;
  const pending = submitting || busy;

  useEffect(() => {
    if (open) {
      setPrice(row.listedPrice != null ? row.listedPrice.toFixed(2) : "");
      setReason(row.cannotListReason ?? "");
      setSecondaryView(null);
      setSubmitting(false);
      setDoubleSaleConfirm(false);
      // Seed the picker from the operator's MANUAL pool pick, never the detected
      // account — the two live in different id-spaces (buyback_accounts vs
      // accounts) and must never be cross-compared.
      setSelectedPoolAccountId(row.buybackSelectedAccountId ?? null);
      setExcludeUsed(true);
      setBuybackCompleting(false);
      setBuybackCompletedLocal(false);
    }
  }, [open, row.listedPrice, row.cannotListReason, row.buybackSelectedAccountId]);

  // NOTE: there is deliberately NO auto-pick-on-open. Auto-persisting the top
  // ranked candidate would mark that pool account as "used" the moment an
  // operator merely opens a row, poisoning the used/unused signal. The manual
  // pick must always be an explicit click (the top eligible candidate is instead
  // flagged "Recommended" in the list below).

  // Close the popup when *this row's own* fired transition settles. We pass the
  // callback through to react-query's per-call `mutate(..., { onSettled })`,
  // which only runs while this component is still mounted. The previous approach
  // watched the shared `fve.busyId` flag, which flips for unrelated rows and can
  // flicker during a background refresh — landing competing setState calls in a
  // single render cycle and tripping React's "too many re-renders" guard (#185).
  const onSettled = () => {
    setSubmitting(false);
    setOpen(false);
  };

  const fromEmail = row.fveStatusSource === "email";
  const emailId = row.fveStatusEmailId ?? null;

  // Open the in-place email viewer and dismiss the popover/sheet so the two
  // overlays never stack.
  const openEmail = () => {
    if (emailId == null) return;
    setOpen(false);
    setEmailModalId(emailId);
  };

  const submitListed = () => {
    const num = price.trim() === "" ? null : Number(price);
    if (num != null && (!Number.isFinite(num) || num < 0)) return;
    setSubmitting(true);
    fve.markListed(row.id, num, { onSettled });
  };

  const submitCannotList = () => {
    const r = reason.trim();
    if (!r) return;
    setSubmitting(true);
    fve.cannotList(row.id, r, { onSettled });
  };

  const flagNeedHelpMutation = useFlagNeedHelp();
  const offerAssistanceMutation = useOfferAssistance();

  const doFlagHelp = () => {
    const note = helpNote.trim();
    if (!note) return;
    setSubmitting(true);
    flagNeedHelpMutation.mutate(
      { id: row.id, data: { note } },
      {
        onSuccess: () => toast.success("Flagged for help — broadcast to all pullers"),
        onError: () => toast.error("Failed to flag for help"),
        onSettled,
      },
    );
  };

  const doOfferAssistance = () => {
    setSubmitting(true);
    offerAssistanceMutation.mutate(
      { id: row.id },
      {
        onSuccess: () => toast.success("You've claimed this listing — coordinate with the puller"),
        onError: () => toast.error("Failed to claim listing"),
        onSettled,
      },
    );
  };

  // True when the current user is the row's original puller (not the assisting puller).
  // Used to gate "Flag for Help" vs "Offer Assistance".
  const isRowOwner =
    me?.pullerTag != null && me.pullerTag === row.pullerTag;

  const doUnlist = () => {
    setSubmitting(true);
    fve.unlist(row.id, { onSettled });
  };

  const doReEnable = () => {
    setSubmitting(true);
    fve.reEnable(row.id, { onSettled });
  };

  const doSetIntent = (intent: NonNullable<BuybackFveIntent>) => {
    // Toggle off when re-selecting the current intent so a row can be cleared.
    const next = currentIntent === intent ? null : intent;
    setSubmitting(true);
    fve.setBuybackFveIntent(row.id, next, { onSettled });
  };

  const submitSurrender = () => {
    setSubmitting(true);
    fve.setBuybackFveIntent(row.id, "surrender", { onSettled });
  };

  const doSelectAccount = (accountId: number | null) => {
    setSelectedPoolAccountId(accountId);
    // Persist immediately so the row retains the selection after the popover
    // closes. Refetch this row's candidate list on settle so the pool's
    // used/unused flags reflect the new assignment right away.
    fve.setBuybackAccount(
      row.id,
      { accountId },
      {
        onSettled: () =>
          queryClient.invalidateQueries({
            queryKey: [`/api/inventory/${row.id}/buyback-candidates`],
          }),
      },
    );
  };

  // Resolve an ambiguous detection by the operator-chosen buyback purchase. The
  // server derives the account from that purchase's vendor and clears ambiguity.
  const doResolveAmbiguous = (purchaseOrderId: string | null) => {
    if (!purchaseOrderId) return;
    setSubmitting(true);
    fve.setBuybackAccount(
      row.id,
      { purchaseOrderId },
      {
        onSettled: () => setSubmitting(false),
        onError: () => toast.error("Failed to link buyback purchase"),
      },
    );
  };

  const doCompleteBuyback = () => {
    // Completion (out of scope for the pool feature) is driven by the auto-
    // DETECTED account, not the operator's manual pool pick. Gate on the
    // detected join so a pool selection never implies a reallocation.
    if (!row.buybackAccountId || buybackCompleted) return;
    setBuybackCompleting(true);
    fve.completeBuyback(row.id, {
      onSuccess: () => {
        setBuybackCompleting(false);
        setBuybackCompletedLocal(true);
        setOpen(false);
        toast.success("Buyback complete");
      },
      onError: () => {
        setBuybackCompleting(false);
        toast.error("Buyback completion failed");
      },
    });
  };

  const doUnmatchSale = () => {
    setSubmitting(true);
    fve.unmatchSale(row.id, {
      onSuccess: () => {
        toast.success("Sale match removed — listing is now open again.");
      },
      onError: () => {
        setSubmitting(false);
        toast.error("Failed to unmatch sale email");
      },
      onSettled,
    });
  };

  const doMove = (force = false) => {
    setSubmitting(true);
    fve.moveToMarketplace(row.id, "reach", force, {
      onSuccess: () => {
        setSubmitting(false);
        setOpen(false);
      },
      onError: (err) => {
        setSubmitting(false);
        // A confirmed sale already exists. Surface the double-sale warning in
        // place (keep the popover open) and require an explicit confirmation,
        // which resends the move with force: true.
        if (isConfirmDoubleSale(err)) {
          setDoubleSaleConfirm(true);
          return;
        }
        toast.error("Failed to move listing to Reach");
      },
    });
  };

  // The status pill that opens the menu — shared by the desktop popover trigger
  // and the mobile bottom-sheet trigger.
  const triggerButton = (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide transition-colors hover:brightness-110",
        meta.className,
      )}
      aria-label={`FVE status: ${meta.label}. Click to change.`}
    >
      {meta.verified && <Check className="h-3 w-3" />}
      {meta.label}
      {meta.discounted && (
        <Percent
          className="h-3 w-3"
          aria-label="Listed at a discount to face value"
        />
      )}
      <ChevronDown className="h-3 w-3 opacity-60" />
    </button>
  );


  // The full menu (header + all action views). Rendered identically inside the
  // desktop popover and the mobile bottom sheet so every action behaves the
  // same. On mobile we reserve room on the right of the header for the sheet's
  // built-in close (X) button.
  const menuBody = (
    <>
      {/* Zone 1 — Header: a colour band matching the pill's status, the
          status label + auto/manual source, and a View-email link when the
          state was driven by an email. Shared by every status. */}
      <div
        className={cn(
          "flex items-center justify-between gap-2 border-b px-3 py-2",
          isMobile && "pr-10",
          meta.className,
        )}
      >
        <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide">
          {meta.verified && <Check className="h-3.5 w-3.5" />}
          {meta.label}
          {meta.discounted && (
            <Percent
              className="h-3 w-3"
              aria-label="Listed below face value"
            />
          )}
        </span>
        <span className="inline-flex items-center gap-0.5 text-[10px] opacity-80">
          {fromEmail ? (
            <>
              <Mail className="h-3 w-3" /> auto
            </>
          ) : (
            "manual"
          )}
        </span>
      </div>

      <div className="p-3">
            {(status === "sold" || status === "sold_partial") &&
            secondaryView === null ? (
              <div className="space-y-2.5">
                <p className="text-[11px] text-muted-foreground">
                  {status === "sold_partial"
                    ? "Only some of this listing's confirmed tickets are sold — the rest are still available."
                    : "This listing is marked Sold from a matched sale email."}
                </p>
                {emailId != null && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-full text-xs"
                    onClick={openEmail}
                  >
                    <Mail className="h-3.5 w-3.5 mr-1" /> View sale email
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
                  disabled={pending}
                  onClick={doUnmatchSale}
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  )}
                  Unmatch sale email
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  This reopens the listing — re-matching may happen on next sync.
                </p>
                {/* Push the remaining (or all) seats back to Reach. Because the
                    row already has a confirmed sale, the server guards this with
                    a double-sale confirmation handled in the move_reach view. */}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full text-xs"
                  disabled={pending}
                  onClick={() => setSecondaryView("move_reach")}
                >
                  <Store className="h-3.5 w-3.5 mr-1" /> Sell in Reach
                </Button>
              </div>
            ) : status === "sale_cancelled" ? (
              /* A confirmed sale was reversed by a Ticketmaster cancellation.
                 The seats are available again; re-listing runs through the
                 normal hold -> list flow (a hold clears this banner), so the
                 affordance here is guidance + a link to the cancellation. */
              <div className="space-y-2.5">
                <p className="text-[11px] text-muted-foreground">
                  Ticketmaster cancelled this sale, so the seats are available
                  again.
                </p>
                <div className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200">
                  <RotateCcw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    To re-list, place a hold on these seats — that clears the
                    cancellation and lets you mark them Listed again.
                  </span>
                </div>
                {emailId != null && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-full text-xs"
                    onClick={openEmail}
                  >
                    <Mail className="h-3.5 w-3.5 mr-1" /> View cancellation
                    email
                  </Button>
                )}
              </div>
            ) : status === "cannot_list" ? (
              <div className="space-y-2.5">
                <p className="text-[11px] text-muted-foreground">
                  Flagged as unable to list. Re-enable once the issue is
                  resolved.
                </p>
                {row.cannotListReason && (
                  <div className="rounded-md border border-border bg-muted/40 p-2">
                    <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Reason
                    </div>
                    <p className="mt-0.5 text-[11px] text-foreground">
                      {row.cannotListReason}
                    </p>
                  </div>
                )}
                {fromEmail && emailId != null && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-full justify-start px-1 text-[11px] text-primary hover:text-primary"
                    onClick={openEmail}
                  >
                    <Mail className="h-3.5 w-3.5 mr-1" /> View email
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full text-xs"
                  disabled={pending}
                  onClick={doReEnable}
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  )}
                  Re-enable
                </Button>
                {/* Secondary route: skip the Re-enable → Move two-step and send a
                    Cannot List row straight to Reach. Clears the flag server-side
                    in the same transaction so the status resolves to via_reach. */}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full rounded-full text-xs"
                  disabled={pending}
                  onClick={() => doMove()}
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <ArrowRight className="h-3.5 w-3.5 mr-1" />
                  )}
                  Sell in Reach
                </Button>
              </div>
            ) : status === "surrender_on_fve" ? (
              <div className="space-y-2.5">
                <p className="text-[11px] text-muted-foreground">
                  This listing has been surrendered — the tickets are being
                  abandoned and cannot be sold or listed.
                </p>
                {row.internalNotes &&
                  /surrender/i.test(row.internalNotes) && (
                    <div className="flex items-start gap-1.5 rounded-md border border-zinc-500/30 bg-zinc-500/10 p-2 text-[11px] text-zinc-300">
                      <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" />
                      <span>
                        Auto-detected: "SURRENDER" found in Custom Notes.
                      </span>
                    </div>
                  )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full text-xs"
                  disabled={pending}
                  onClick={doReEnable}
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  )}
                  Re-enable
                </Button>
              </div>
            ) : secondaryView === "cannot_list" ? (
              /* Inline mini-form: Cannot List with a labelled reason. */
              <div className="space-y-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => setSecondaryView(null)}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <div className="space-y-1.5">
                  <label
                    htmlFor={`cannot-list-${row.id}`}
                    className="text-[10px] font-medium text-muted-foreground"
                  >
                    Why can't this be listed?
                  </label>
                  <Textarea
                    id={`cannot-list-${row.id}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. bad phone, banking issue"
                    className="min-h-[60px] text-xs"
                    aria-label="Cannot list reason"
                  />
                  <Button
                    size="sm"
                    variant={reason.trim() ? "destructive" : "outline"}
                    className="h-8 w-full text-xs"
                    disabled={!reason.trim() || pending}
                    onClick={submitCannotList}
                  >
                    {pending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Ban className="h-3.5 w-3.5 mr-1" />
                    )}
                    Mark Cannot List
                  </Button>
                  <p className="text-[10px] text-muted-foreground">
                    This prevents auto-listing until re-enabled.
                  </p>
                </div>
              </div>
            ) : secondaryView === "surrender" ? (
              <div className="space-y-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => setSecondaryView(null)}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <div className="flex items-start gap-1.5 rounded-md border border-zinc-500/30 bg-zinc-500/10 p-2 text-[11px] text-zinc-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Surrendering marks these tickets as abandoned. They cannot
                    be sold, bought back, or listed until re-enabled.
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-full text-xs border-zinc-500/40 text-zinc-300 hover:bg-zinc-500/20"
                  disabled={pending}
                  onClick={submitSurrender}
                >
                  {pending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <HandCoins className="h-3.5 w-3.5 mr-1" />
                  )}
                  Confirm Surrender
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  This can be undone with Re-enable at any time.
                </p>
              </div>
            ) : secondaryView === "flag_help" ? (
              /* Inline mini-form: flag this listing for help from other pullers. */
              <div className="space-y-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => setSecondaryView(null)}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <div className="space-y-1.5">
                  <label
                    htmlFor={`help-note-${row.id}`}
                    className="text-[10px] font-medium text-muted-foreground"
                  >
                    What help do you need?
                  </label>
                  <Textarea
                    id={`help-note-${row.id}`}
                    value={helpNote}
                    onChange={(e) => setHelpNote(e.target.value)}
                    placeholder="e.g. can't find a good buyback account for this one"
                    className="min-h-[60px] text-xs"
                    aria-label="Help note"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    variant={helpNote.trim() ? "default" : "outline"}
                    className="h-8 w-full text-xs"
                    disabled={!helpNote.trim() || pending}
                    onClick={doFlagHelp}
                  >
                    {pending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Hand className="h-3.5 w-3.5 mr-1" />
                    )}
                    Broadcast for Help
                  </Button>
                  <p className="text-[10px] text-muted-foreground">
                    This listing will appear in all pullers' views until
                    someone claims it.
                  </p>
                </div>
              </div>
            ) : secondaryView === "move_reach" ? (
              /* Inline confirm: moving to Reach is an intentional warning step.
                 For already-sold rows the server rejects the first attempt with
                 a double-sale 409, which flips this into a stronger warning that
                 resends with force. */
              <div className="space-y-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => {
                    setSecondaryView(null);
                    setDoubleSaleConfirm(false);
                  }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                {doubleSaleConfirm ? (
                  <>
                    <div className="flex items-start gap-1.5 rounded-md border border-red-500/50 bg-red-500/10 p-2 text-[11px] text-red-200">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        This listing already has a confirmed sale. Moving it to
                        Reach may sell the same tickets twice. Only proceed if you
                        are certain the remaining seats are still available.
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-8 w-full text-xs"
                      disabled={pending}
                      onClick={() => doMove(true)}
                    >
                      {pending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Store className="h-3.5 w-3.5 mr-1" />
                      )}
                      Yes, sell in Reach anyway
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        Remove this listing from the Fair Value Exchange first, or
                        it could sell in two places at once.
                      </span>
                    </div>
                    <Button
                      size="sm"
                      className="h-8 w-full text-xs"
                      disabled={pending}
                      onClick={() => doMove(false)}
                    >
                      {pending ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Store className="h-3.5 w-3.5 mr-1" />
                      )}
                      Confirm move to Reach
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2.5">
                {fromEmail && emailId != null && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-full justify-start px-1 text-[11px] text-primary hover:text-primary"
                    onClick={openEmail}
                  >
                    <Mail className="h-3.5 w-3.5 mr-1" /> View email
                  </Button>
                )}

                {/* Zone 2 — Primary action: set / edit the FVE price. */}
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <label
                      htmlFor={`fve-price-${row.id}`}
                      className="text-[10px] font-medium text-muted-foreground"
                    >
                      FVE list price
                    </label>
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">
                      {row.listedPrice != null
                        ? `${sym}${row.listedPrice.toFixed(2)}`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-muted-foreground">
                      {sym}
                    </span>
                    <Input
                      id={`fve-price-${row.id}`}
                      type="number"
                      min={0}
                      step="0.01"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="FVE list price (optional)"
                      className="h-8 text-xs flex-1"
                      aria-label="FVE list price"
                      disabled={pending}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submitListed();
                      }}
                    />
                  </div>
                  {!canList && (
                    <p className="text-[10px] text-amber-400">
                      Place a hold before marking as listed.
                    </p>
                  )}
                  <Button
                    size="sm"
                    className="h-8 w-full text-xs"
                    disabled={!canList || pending}
                    onClick={submitListed}
                  >
                    {pending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Tag className="h-3.5 w-3.5 mr-1" />
                    )}
                    {isListed ? "Save price" : "Mark Listed"}
                  </Button>
                  {isListed && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-full text-xs border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      disabled={pending}
                      onClick={doUnlist}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Unlist
                    </Button>
                  )}
                </div>

                {/* Zone 3 — Secondary actions: always-visible icon tiles,
                    shown only when valid for the current state. */}
                <div className="border-t border-border pt-2.5">
                  <div
                    className={cn(
                      "grid gap-1.5",
                      showIntentTiles || movable
                        ? "grid-cols-4"
                        : "grid-cols-2",
                    )}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="flex flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/30 px-1.5 py-2 text-[10px] font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                          disabled={pending}
                          onClick={() => setSecondaryView("cannot_list")}
                        >
                          <Ban className="h-4 w-4" />
                          <span>Cannot List</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Flags this listing as one that can't go on the FVE (e.g.
                        a bad phone or banking issue), removing it from the FVE
                        and preventing auto-listing until it's resolved.
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="flex flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/30 px-1.5 py-2 text-[10px] font-medium text-muted-foreground transition-colors hover:border-zinc-500/40 hover:bg-zinc-500/10 hover:text-zinc-300 disabled:opacity-50"
                          disabled={pending}
                          onClick={() => setSecondaryView("surrender")}
                        >
                          <HandCoins className="h-4 w-4" />
                          <span>Surrender</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Marks this listing as surrendered — the tickets cannot
                        be sold, bought back, or listed until re-enabled.
                      </TooltipContent>
                    </Tooltip>
                    {showIntentTiles ? (
                      <>
                        {/* Buyback-display mode: Zone-3 marketplace tiles are
                            replaced with an explicit FVE intent picker. */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "flex flex-col items-center justify-center gap-1 rounded-md border px-1.5 py-2 text-[10px] font-medium transition-colors disabled:opacity-50",
                                currentIntent === "buy_back"
                                  ? INTENT_COLORS.buy_back
                                  : "border-border bg-muted/30 text-muted-foreground hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-400",
                              )}
                              disabled={pending}
                              onClick={() => doSetIntent("buy_back")}
                            >
                              <RotateCcw className="h-4 w-4" />
                              <span>Buy Back</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Mark this listing's FVE intent as Buy Back. Click
                            again to clear.
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "flex flex-col items-center justify-center gap-1 rounded-md border px-1.5 py-2 text-[10px] font-medium transition-colors disabled:opacity-50",
                                currentIntent === "sell"
                                  ? INTENT_COLORS.sell
                                  : "border-border bg-muted/30 text-muted-foreground hover:border-emerald-500/40 hover:bg-emerald-500/10 hover:text-emerald-400",
                              )}
                              disabled={pending}
                              onClick={() => doSetIntent("sell")}
                            >
                              <Tag className="h-4 w-4" />
                              <span>Sell</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Mark this listing's FVE intent as Sell. Click again
                            to clear.
                          </TooltipContent>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        {movable && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="flex flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/30 px-1.5 py-2 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                                disabled={pending}
                                onClick={() => setSecondaryView("move_reach")}
                              >
                                <Store className="h-4 w-4" />
                                <span>Sell in Reach</span>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Releases any active hold and marks this listing as
                              selling via Reach, removing it from FVE.
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {movable && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className="flex cursor-not-allowed flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/30 px-1.5 py-2 text-[10px] font-medium text-muted-foreground opacity-50"
                                aria-disabled="true"
                              >
                                <Store className="h-4 w-4" />
                                <span>Sell in Skybox</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              Sell in Skybox · Coming soon
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Zone 5 — Need Help / Offer Assistance. For buy_back rows:
                    the row's owner can flag it for help; other pullers can
                    claim it. Only visible when secondaryView is null. */}
                {secondaryView === null && (
                  <>
                    {/* Row owner with buy_back intent: flag / status / unflag */}
                    {showAccountPicker && isRowOwner && (
                      <div className="border-t border-border pt-2.5">
                        {!row.needHelp ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-full rounded-full text-xs border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
                            disabled={pending}
                            onClick={() => {
                              setHelpNote("");
                              setSecondaryView("flag_help");
                            }}
                          >
                            <Hand className="h-3.5 w-3.5 mr-1" />
                            Need Help
                          </Button>
                        ) : row.needHelpPullerId == null ? (
                          <div className="space-y-2">
                            <div className="flex items-start gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 p-2 text-[11px] text-orange-200">
                              <Hand className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              <div className="space-y-0.5">
                                <span className="font-medium">Broadcast for help</span>
                                {row.needHelpNote && (
                                  <p className="text-[10px] text-orange-200/80">
                                    {row.needHelpNote}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 w-full text-[11px] text-muted-foreground hover:text-foreground"
                              disabled={pending}
                              onClick={() => {
                                setHelpNote(row.needHelpNote ?? "");
                                setSecondaryView("flag_help");
                              }}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Update note
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-start gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1.5 text-[11px] text-emerald-300">
                            <UserCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <div className="space-y-0.5">
                              <span className="font-medium">Help claimed</span>
                              {(row.needHelpPullerName || row.needHelpPullerTag) && (
                                <p className="text-[10px] text-emerald-300/80">
                                  by {row.needHelpPullerName ?? row.needHelpPullerTag}
                                  {row.needHelpPullerTag && row.needHelpPullerName && ` (${row.needHelpPullerTag})`}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Different puller seeing a need_help broadcast row */}
                    {status === "need_help" && !isRowOwner && me?.pullerId != null && (
                      <div className="border-t border-border pt-2.5 space-y-2">
                        <div className="rounded-md border border-orange-500/40 bg-orange-500/10 p-2 space-y-1">
                          <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-orange-400">
                            <Hand className="h-3 w-3" />
                            <span>Puller needs assistance</span>
                          </div>
                          {row.needHelpNote && (
                            <p className="text-[11px] text-orange-200/80">
                              {row.needHelpNote}
                            </p>
                          )}
                        </div>
                        {/* After another puller has already claimed it, show their identity */}
                        {row.needHelpPullerId != null ? (
                          <div className="flex items-start gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1.5 text-[11px] text-emerald-300">
                            <UserCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <div className="space-y-0.5">
                              <span className="font-medium">Already claimed</span>
                              {(row.needHelpPullerName || row.needHelpPullerTag) && (
                                <p className="text-[10px] text-emerald-300/80">
                                  by {row.needHelpPullerName ?? row.needHelpPullerTag}
                                  {row.needHelpPullerTag && row.needHelpPullerName && ` (${row.needHelpPullerTag})`}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              className="h-8 w-full text-xs bg-orange-600 hover:bg-orange-500 text-white"
                              disabled={pending}
                              onClick={doOfferAssistance}
                            >
                              {pending ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <UserCheck className="h-3.5 w-3.5 mr-1" />
                              )}
                              Offer Assistance
                            </Button>
                            <p className="text-[10px] text-muted-foreground">
                              Claiming this will move it into your workspace.
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Zone 4 — Buyback account picker. Shown only when the row's
                    FVE intent is 'buy_back'; presents a ranked candidate list
                    so the operator can assign an account and complete the
                    reallocation. */}
                {showAccountPicker && (
                  <div className="border-t border-border pt-2.5 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        <Users className="h-3 w-3" />
                        <span>Buyback account</span>
                      </div>
                      {/* Hide/show pool accounts already applied elsewhere. */}
                      <button
                        type="button"
                        onClick={() => setExcludeUsed((v) => !v)}
                        className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
                      >
                        <span
                          className={cn(
                            "h-3 w-3 rounded-sm border flex items-center justify-center",
                            excludeUsed
                              ? "border-amber-500/60 bg-amber-500/20 text-amber-300"
                              : "border-border",
                          )}
                        >
                          {excludeUsed && <Check className="h-2.5 w-2.5" />}
                        </span>
                        <span>Hide used</span>
                      </button>
                    </div>
                    {/* Currently-applied manual pick, resolved from the row so it
                        survives a popover reopen even before the list loads. */}
                    {row.buybackSelectedAccountEmail && (
                      <div className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-300">
                        <UserCheck className="h-3 w-3 shrink-0" />
                        <span className="min-w-0 truncate">
                          Applied: {row.buybackSelectedAccountEmail}
                        </span>
                      </div>
                    )}
                    {/* Ambiguous detection — operator must pick the buyback
                        purchase; the row is never auto-linked in this state. */}
                    {ambiguous && (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 space-y-1.5">
                        <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-amber-400">
                          <AlertTriangle className="h-3 w-3" />
                          <span>Multiple matches — pick the buyback PO</span>
                        </div>
                        {matchesQuery.isLoading ? (
                          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>Loading matches…</span>
                          </div>
                        ) : matches.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground">
                            No matches found
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {matches.map((m, i) => (
                              <button
                                key={m.purchaseOrderId ?? `m-${i}`}
                                type="button"
                                disabled={pending}
                                onClick={() =>
                                  doResolveAmbiguous(m.purchaseOrderId)
                                }
                                className="w-full flex flex-col items-start gap-0.5 rounded-md border border-border bg-muted/20 px-2 py-1.5 text-left text-[11px] hover:border-amber-500/40 hover:bg-amber-500/10 disabled:opacity-50"
                              >
                                <span className="font-mono text-amber-300">
                                  PO {m.purchaseOrderId ?? "—"}
                                </span>
                                <span className="min-w-0 w-full truncate text-muted-foreground">
                                  {m.vendorAccountName ||
                                    m.vendorAccountEmail ||
                                    "Unknown account"}
                                </span>
                                <span className="text-[9px] text-muted-foreground">
                                  {[
                                    m.section && `Sec ${m.section}`,
                                    m.row && `Row ${m.row}`,
                                    m.seats.length > 0 && m.seats.join(", "),
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {candidatesQuery.isLoading ? (
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>Loading candidates…</span>
                      </div>
                    ) : candidatesQuery.isError ? (
                      <p className="text-[11px] text-destructive">
                        Failed to load candidates
                      </p>
                    ) : candidates.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground">
                        No accounts found for this puller
                      </p>
                    ) : eligibleCandidates.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground">
                        {hiddenUsedCount > 0
                          ? `All eligible accounts are already used — toggle "Hide used" to pick one, or choose an override below.`
                          : "No accounts meet the auto-select criteria — pick one manually below."}
                      </p>
                    ) : (
                      <div className="max-h-36 overflow-y-auto space-y-1">
                        {eligibleCandidates.map((c) => {
                          const isSel = selectedPoolAccountId === c.id;
                          const label = c.displayName ?? c.vendorAccountEmail;
                          const tier =
                            c.purchaseCount === 0
                              ? "text-emerald-400"
                              : c.purchaseCount < 3
                                ? "text-amber-400"
                                : "text-orange-400";
                          const phoneDot =
                            c.phoneStatus === "good"
                              ? "bg-emerald-400"
                              : c.phoneStatus === "bad"
                                ? "bg-red-400"
                                : "bg-muted-foreground";
                          return (
                            <button
                              key={c.id}
                              type="button"
                              className={cn(
                                "w-full flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-left text-[11px] transition-colors",
                                isSel
                                  ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                                  : "border-border bg-muted/20 text-foreground hover:border-amber-500/30 hover:bg-amber-500/10",
                              )}
                              disabled={pending || buybackCompleting || buybackCompleted}
                              onClick={() =>
                                doSelectAccount(isSel ? null : c.id)
                              }
                            >
                              {isSel ? (
                                <UserCheck className="h-3.5 w-3.5 shrink-0 text-amber-400" />
                              ) : (
                                <span
                                  className={cn(
                                    "h-3.5 w-3.5 shrink-0 rounded-full border text-[8px] flex items-center justify-center font-bold",
                                    tier,
                                  )}
                                  title={`${c.purchaseCount} TM buy${c.purchaseCount === 1 ? "" : "s"}`}
                                >
                                  {c.purchaseCount}
                                </span>
                              )}
                              <span className="min-w-0 truncate flex-1">
                                {label}
                              </span>
                              {c.id === recommendedId && !isSel && (
                                <span className="shrink-0 rounded-sm bg-emerald-500/20 px-1 text-[8px] font-semibold uppercase tracking-wide text-emerald-300">
                                  Rec
                                </span>
                              )}
                              {c.used && (
                                <span
                                  className="shrink-0 rounded-sm bg-muted px-1 text-[8px] font-semibold uppercase tracking-wide text-muted-foreground"
                                  title="Already applied to another purchase"
                                >
                                  Used
                                </span>
                              )}
                              <span
                                className={cn(
                                  "h-1.5 w-1.5 shrink-0 rounded-full",
                                  phoneDot,
                                )}
                                title={`Phone ${c.phoneStatus}`}
                              />
                              <span
                                className="text-[9px] text-muted-foreground shrink-0"
                                title="Email last verified"
                              >
                                {formatVerified(c.lastVerified)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Ineligible accounts — each fails an auto-select gate
                        (bound out, phone, verification freshness), so they are
                        never auto-picked. An operator can still assign one as a
                        manual override: the server accepts any account belonging
                        to the listing's puller. Auto-expanded when nothing
                        qualified so the manual path is obvious. */}
                    {excludedCandidates.length > 0 && (
                      <details
                        className="text-[10px]"
                        open={eligibleCandidates.length === 0}
                      >
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                          {excludedCandidates.length} ineligible (pick to
                          override)
                        </summary>
                        <div className="mt-1 max-h-36 overflow-y-auto space-y-1">
                          {excludedCandidates.map((c) => {
                            const isSel = selectedPoolAccountId === c.id;
                            const label = c.displayName ?? c.vendorAccountEmail;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                disabled={
                                  pending ||
                                  buybackCompleting ||
                                  buybackCompleted
                                }
                                onClick={() =>
                                  doSelectAccount(isSel ? null : c.id)
                                }
                                className={cn(
                                  "w-full flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-left text-[11px] transition-colors disabled:opacity-50",
                                  isSel
                                    ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                                    : "border-border/60 bg-muted/10 text-muted-foreground hover:border-amber-500/30 hover:bg-amber-500/10",
                                )}
                              >
                                {isSel ? (
                                  <UserCheck className="h-3.5 w-3.5 shrink-0 text-amber-400" />
                                ) : (
                                  <span
                                    className="h-3.5 w-3.5 shrink-0 rounded-full border border-muted-foreground/40 text-[8px] flex items-center justify-center font-bold text-muted-foreground"
                                    title={`${c.purchaseCount} TM buy${c.purchaseCount === 1 ? "" : "s"}`}
                                  >
                                    {c.purchaseCount}
                                  </span>
                                )}
                                <span className="min-w-0 truncate flex-1">
                                  {label}
                                </span>
                                {c.boundOut && (
                                  <span className="shrink-0 text-[9px] uppercase text-red-400">
                                    bound out
                                  </span>
                                )}
                                <span className="shrink-0 text-[9px] uppercase">
                                  {excludeReasonLabel(c.excludeReason)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </details>
                    )}
                    <Button
                      size="sm"
                      className="h-8 w-full text-xs bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
                      disabled={
                        !row.buybackAccountId ||
                        pending ||
                        buybackCompleting ||
                        buybackCompleted
                      }
                      onClick={doCompleteBuyback}
                    >
                      {buybackCompleting ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                          Completing…
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          Complete Buyback
                        </>
                      )}
                    </Button>
                    {!row.buybackAccountId && (
                      <p className="text-[9px] text-muted-foreground text-center">
                        Completion requires a detected buyback purchase — the
                        manual pick above only records the intended account.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
      </div>
    </>
  );


  return (
    <div className="inline-flex items-center gap-1">
      {isMobile ? (
        /* On phones, render the menu as a bottom sheet so the tile grid,
           price zone and mini-forms get full width without clipping or
           horizontal scroll. */
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>{triggerButton}</SheetTrigger>
          <SheetContent
            side="bottom"
            className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-xl p-0"
          >
            <SheetTitle className="sr-only">Change FVE status</SheetTitle>
            {menuBody}
          </SheetContent>
        </Sheet>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          {/* Tooltip is forced closed while the popover is open so the two Radix
              layers never stack on top of each other. */}
          <Tooltip open={tipOpen && !open} onOpenChange={setTipOpen}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[260px] p-0">
              <div className="space-y-1 px-3 py-2">
                <div className="text-xs font-semibold">{tip.title}</div>
                {tip.lines.map((line, i) => (
                  <p key={i} className="text-[11px] leading-snug opacity-90">
                    {line}
                  </p>
                ))}
              </div>
              <div className="space-y-1 border-t border-border px-3 py-2 text-[10px] leading-snug">
                <div className="font-medium uppercase tracking-wide opacity-70">
                  Key
                </div>
                <div className="flex items-start gap-1">
                  <Check className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>
                    Bright colour + checkmark = confirmed by a Reach email
                  </span>
                </div>
                <div className="opacity-90">
                  Muted black = operator-set, not yet email-confirmed
                </div>
                <div className="flex items-start gap-1">
                  <Percent className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>Percent icon = listed below face value</span>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
          <PopoverContent align="start" className="w-72 p-0 overflow-hidden">
            {menuBody}
          </PopoverContent>
        </Popover>
      )}
      <IssueWarning row={row} />
      {status === "sold" && (row.confirmedSaleCount ?? 0) > 1 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link href={`/sales?inventory=${row.id}`}>
              <span
                className="inline-flex items-center gap-0.5 rounded-full border border-purple-500/40 bg-purple-500/15 px-1.5 py-0.5 text-[10px] font-medium text-purple-300 hover:brightness-110"
                aria-label={`${row.confirmedSaleCount} sales — view in Sales`}
              >
                <ShoppingBag className="h-3 w-3" />
                {row.confirmedSaleCount}
              </span>
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            {row.confirmedSaleCount} confirmed sales
            {(row.soldTicketCount ?? 0) > 0
              ? ` · ${row.soldTicketCount} tickets`
              : ""}{" "}
            — view in Sales
          </TooltipContent>
        </Tooltip>
      )}
      <EmailViewerModal
        emailId={emailModalId}
        onClose={() => setEmailModalId(null)}
      />
    </div>
  );
}

// A matched email is a "sale" (purple) when it records an actual sale; listing /
// payment / delivery confirmations read as "listing" (emerald) provenance.
function emailIsSale(t: string): boolean {
  return t === "sale_notification" || t === "sale_confirmation";
}

function emailTypeLabel(t: string): string {
  switch (t) {
    case "sale_notification":
      return "Sale";
    case "sale_confirmation":
      return "Sale conf.";
    case "listing_confirmation":
      return "Listing";
    case "payment":
      return "Payment";
    case "delivery":
      return "Delivery";
    default:
      return "Email";
  }
}

// Compact relative time ("3h ago") that falls back to an absolute date for
// anything older than a month. The absolute datetime is shown on hover via the
// caller's title attribute.
export function formatRelativeTime(s?: string | null): string {
  if (!s) return "-";
  const d = new Date(s);
  const t = d.getTime();
  if (Number.isNaN(t)) return "-";
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function EmailHoverRow({ email }: { email: InventoryEmailItem }) {
  const sale = emailIsSale(email.emailType);
  const sym = currencySymbol(email.currency);
  const price = sale ? email.payoutPerTicket : email.listedPrice;
  const id = sale ? (email.orderNumber ?? email.listingNumber) : email.listingNumber;
  const seating =
    [
      email.section ? `Sec ${email.section}` : null,
      email.row ? `Row ${email.row}` : null,
      email.seats.length ? email.seats.join(", ") : null,
    ]
      .filter(Boolean)
      .join(" · ") || "-";
  return (
    <tr className="border-t border-border/60 align-top">
      <td className="px-2 py-1.5">
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-medium uppercase",
            sale
              ? "bg-purple-500/15 text-purple-300 border-purple-500/40"
              : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
          )}
        >
          {emailTypeLabel(email.emailType)}
        </span>
      </td>
      <td
        className="px-2 py-1.5 whitespace-nowrap text-muted-foreground"
        title={formatHoldDate(email.receivedAt) ?? ""}
      >
        {formatRelativeTime(email.receivedAt)}
      </td>
      <td className="px-2 py-1.5">
        <div className="font-medium text-foreground leading-tight">
          {email.eventName ?? "-"}
        </div>
        {email.eventDateText && (
          <div className="text-[10px] text-muted-foreground leading-tight">
            {email.eventDateText}
          </div>
        )}
        {email.recipientEmail && (
          <div
            className="max-w-[150px] truncate text-[9px] leading-tight text-muted-foreground/80"
            title={email.recipientEmail}
          >
            {email.recipientEmail}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
        {seating}
      </td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        <div className="font-medium text-foreground">
          {price != null ? `${sym}${price.toFixed(2)}` : "-"}
          {sale && price != null && (
            <span className="ml-0.5 text-[9px] text-muted-foreground">/tkt</span>
          )}
        </div>
        {id && <div className="font-mono text-[9px] text-muted-foreground">{id}</div>}
      </td>
    </tr>
  );
}

// Mail icon in the Actions column for any listing that has matched emails. On
// hover it lazily loads the matched listing/sale emails and shows a compact
// side-by-side table; clicking deep-links to the inbox (single) or Sales
// (multi-sale). Renders nothing when the listing has no emails.
export function ListingEmailsCell({ row }: { row: InventoryListing }) {
  const hasEmail =
    row.fveStatusEmailId != null || (row.confirmedSaleCount ?? 0) > 0;
  const [open, setOpen] = useState(false);
  const [emailModalId, setEmailModalId] = useState<number | null>(null);
  const { data, isLoading } = useListInventoryEmails(row.id, undefined, {
    query: { enabled: open, queryKey: getListInventoryEmailsQueryKey(row.id) },
  });

  if (!hasEmail) return null;

  const saleCount = row.confirmedSaleCount ?? 0;
  // Multi-sale rows still deep-link to the Sales audit; single-email rows open
  // the source email in-place via a modal instead of navigating to the inbox.
  const isMultiSale = saleCount > 1;

  // Synthetic reach-only sale rows carry no real email — exclude them from the
  // "matched emails" panel and its listed/sold counts (they belong to the Sales
  // cell, which renders them separately).
  const items = (data ?? []).filter((e) => e.reachOnly !== true);
  const listedCount = items.filter((e) => !emailIsSale(e.emailType)).length;
  const soldCount = items.filter((e) => emailIsSale(e.emailType)).length;

  const iconInner = (
    <span
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className="relative inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
      aria-label="View matched emails"
    >
      <Mail className="h-3.5 w-3.5" />
      {isMultiSale && (
        <span className="absolute -right-1 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-purple-500/90 px-0.5 text-[8px] font-semibold text-white">
          {saleCount}
        </span>
      )}
    </span>
  );

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {isMultiSale ? (
          <Link href={`/sales?inventory=${row.id}`}>{iconInner}</Link>
        ) : (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setEmailModalId(row.fveStatusEmailId ?? null);
            }}
          >
            {iconInner}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-[480px] overflow-hidden p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-semibold text-foreground">
            Matched emails
          </span>
          <span className="flex items-center gap-2 text-[10px] uppercase tracking-wide">
            {listedCount > 0 && (
              <span className="text-emerald-300">{listedCount} listed</span>
            )}
            {soldCount > 0 && (
              <span className="text-purple-300">{soldCount} sold</span>
            )}
          </span>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted-foreground">
            No matched emails.
          </div>
        ) : (
          <div className="max-h-[320px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-popover">
                <tr className="text-left text-[9px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-1 font-medium">Type</th>
                  <th className="px-2 py-1 font-medium">Received</th>
                  <th className="px-2 py-1 font-medium">Event / Sent to</th>
                  <th className="px-2 py-1 font-medium">Seating</th>
                  <th className="px-2 py-1 text-right font-medium">Price / ID</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <EmailHoverRow key={e.emailId} email={e} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PopoverContent>
    </Popover>
    <EmailViewerModal
      emailId={emailModalId}
      onClose={() => setEmailModalId(null)}
    />
    </>
  );
}

// Human-readable label for a raw email_type, used in the inline email viewer.
const EMAIL_TYPE_LABEL_FULL: Record<string, string> = {
  listing_confirmation: "Listing",
  sale_notification: "Sale (notification)",
  sale_confirmation: "Sale (confirmation)",
  delivery: "Delivery",
  payment: "Payment",
  unknown: "Unknown",
};

// Reusable inline email viewer. Fetches a single listing/sale email by id and
// renders its metadata + raw body inside a Sheet, so operators can read the
// source email without leaving the inventory table. Stays dormant (no fetch)
// while closed or when emailId is null.
export function EmailViewSheet({
  emailId,
  open,
  onClose,
}: {
  emailId: number | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: detail, isLoading, isError } = useGetSaleEmail(emailId ?? 0, {
    query: {
      enabled: open && emailId != null,
      queryKey: getGetSaleEmailQueryKey(emailId ?? 0),
    },
  });
  const email = detail?.email;
  const sym = currencySymbol(email?.currency);
  const seating =
    [
      email?.section ? `Sec ${email.section}` : null,
      email?.row ? `Row ${email.row}` : null,
      email?.seats?.length ? email.seats.join(", ") : null,
    ]
      .filter(Boolean)
      .join(" · ") || "—";
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        {isError ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 px-6 text-center">
            <p className="text-sm font-medium text-foreground">
              Couldn't load this email
            </p>
            <p className="text-xs text-muted-foreground">
              Something went wrong fetching the email. Close this panel and try
              again.
            </p>
          </div>
        ) : isLoading || !detail ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {email?.eventName ?? "Email"}
                {email?.emailType && (
                  <Badge variant="outline">
                    {EMAIL_TYPE_LABEL_FULL[email.emailType] ?? email.emailType}
                  </Badge>
                )}
              </SheetTitle>
              <SheetDescription>
                Listing #{email?.listingNumber ?? "—"}
                {email?.receivedAt
                  ? ` · ${formatHoldDate(email.receivedAt) ?? ""}`
                  : ""}
                {email?.recipientEmail ? ` · ${email.recipientEmail}` : ""}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Seating
                  </div>
                  <div className="font-mono text-foreground">{seating}</div>
                </div>
                {email?.quantity != null && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Quantity
                    </div>
                    <div className="font-mono text-foreground">
                      {email.quantity}
                    </div>
                  </div>
                )}
                {email?.payoutPerTicket != null && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Payout / ticket
                    </div>
                    <div className="font-mono text-foreground">
                      {sym}
                      {email.payoutPerTicket.toFixed(2)}
                    </div>
                  </div>
                )}
                {email?.listedPrice != null && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Listed price
                    </div>
                    <div className="font-mono text-foreground">
                      {sym}
                      {email.listedPrice.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              {detail.rawBody ? (
                <div className="rounded-md border border-border p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-secondary-foreground">
                    Email body
                  </div>
                  <pre className="mt-2 max-h-[60vh] overflow-auto whitespace-pre-wrap text-[11px] text-secondary-foreground">
                    {detail.rawBody}
                  </pre>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No body available for this email.
                </p>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// Reach broadcast status. Distinguishes a confirmed "Yes"/"No" from an
// "unknown" (not yet refreshed) state — null reads as a muted dash, never a
// confident "off". When broadcasting, a tooltip names the merchants if known.
export function BroadcastCell({ row }: { row: InventoryListing }) {
  const state = row.reachBroadcast;
  if (state == null) {
    return (
      <span
        className="font-mono text-[10px] uppercase text-muted-foreground/50"
        title="Broadcast status not yet refreshed from Reach"
      >
        —
      </span>
    );
  }
  if (!state) {
    return (
      <span className="font-mono text-[10px] uppercase text-muted-foreground">
        No
      </span>
    );
  }
  const merchants = row.reachBroadcastMarketplaces?.trim();
  const badge = (
    <Badge
      variant="outline"
      className="gap-1 border-orange-500/30 bg-orange-500/10 font-mono text-[9px] uppercase text-orange-400"
    >
      <Radio className="h-3 w-3" /> Yes
    </Badge>
  );
  if (!merchants) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">
        <div className="text-[11px]">Broadcasting on: {merchants}</div>
      </TooltipContent>
    </Tooltip>
  );
}

// "QR" column cell: real per-ticket uploaded-asset (PDF/QR) coverage from
// Reach. A solid icon means every ticket on the listing has a PDF uploaded;
// a muted/dashed icon means some but not all do; an empty cell means none do.
// Hovering opens a card that lists each ticket and, for the ones that have a
// PDF, shows an inline preview of the actual file. Each preview is fetched live
// from Reach on demand (GET /api/inventory/{id}/assets/{ticketId}/pdf) and is
// never stored — the hover card just embeds that URL in an <iframe>.
// Derive a listing's QR/ticket-asset coverage state from its `ticketAssets`:
//   'none'    — no ticket has a PDF (empty assets or every `hasPdf` false)
//   'full'    — every relevant ticket has a PDF
//   'partial' — some but not all relevant tickets have a PDF
// Coverage is measured against the unsold tickets only when any are sold, since
// sold tickets never carry a PDF in Reach (mirrors QrCoverageCell exactly).
export type QrCoverage = "full" | "partial" | "none";

export function getQrCoverage(row: InventoryListing): QrCoverage {
  const assets = row.ticketAssets ?? [];
  const withPdf = assets.filter((a) => a.hasPdf).length;
  if (withPdf === 0) return "none";
  const hasSold = assets.some((a) => a.sold);
  const denominator = hasSold ? assets.filter((a) => !a.sold).length : assets.length;
  return withPdf >= denominator ? "full" : "partial";
}

export function QrCoverageCell({ row }: { row: InventoryListing }) {
  const assets = row.ticketAssets ?? [];
  // Coverage is computed over every ticket on the listing: each entry carries a
  // `hasPdf` boolean from Reach's /assets endpoint. `withPdf === 0` means nothing
  // is uploaded yet, so the cell stays empty (no icon).
  const total = assets.length;
  const withPdf = assets.filter((a) => a.hasPdf).length;
  if (withPdf === 0) return null;
  // Sold tickets never have a PDF loaded in Reach, so "full coverage" should be
  // measured against the unsold tickets only — otherwise a listing where every
  // remaining ticket has a PDF would still read as partial. Fall back to the
  // total when there are no sold entries so listings without sold tickets behave
  // exactly as before.
  const hasSold = assets.some((a) => a.sold);
  const unsoldAssets = assets.filter((a) => !a.sold);
  const denominator = hasSold ? unsoldAssets.length : total;
  const full = withPdf >= denominator;
  return (
    <HoverCard openDelay={150} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex items-center justify-center"
          aria-label={
            full
              ? hasSold
                ? "All unsold tickets have a PDF asset"
                : "All tickets have a PDF asset"
              : `${withPdf} of ${denominator} ${
                  hasSold ? "unsold " : ""
                }tickets have a PDF asset`
          }
        >
          <QrCode
            className={cn(
              "h-4 w-4",
              full
                ? "text-emerald-400"
                : "text-muted-foreground/60 [stroke-dasharray:2_2]",
            )}
            strokeWidth={full ? 2 : 1.5}
          />
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="center" className="w-80 max-w-[20rem] p-0">
        <div className="px-3 py-2 border-b border-border/60">
          <p className="text-[11px] font-medium text-foreground">
            {full ? "QR assets — full coverage" : "QR assets — partial coverage"}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {withPdf} of {denominator} {hasSold ? "unsold " : ""}ticket
            {denominator === 1 ? "" : "s"} have a PDF
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto p-2 space-y-2">
          {assets.map((a, i) => {
            const label = a.seat ? `Seat ${a.seat}` : `Ticket ${i + 1}`;
            const pdfUrl =
              a.hasPdf && a.ticketId
                ? `/api/inventory/${row.id}/assets/${encodeURIComponent(
                    a.ticketId,
                  )}/pdf`
                : null;
            return (
              <div
                key={a.ticketId ?? `ticket-${i}`}
                className="rounded-md border border-border/60 bg-muted/30"
              >
                <div className="flex items-center justify-between gap-3 px-2 py-1 text-[11px]">
                  <span className="text-foreground truncate">
                    {label}
                    {a.sold ? (
                      <span className="text-muted-foreground"> (sold)</span>
                    ) : null}
                  </span>
                  {a.hasPdf ? (
                    pdfUrl ? (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 font-medium text-emerald-400 hover:underline"
                      >
                        Open ↗
                      </a>
                    ) : (
                      <span className="shrink-0 font-medium text-emerald-400">
                        PDF
                      </span>
                    )
                  ) : (
                    <span className="shrink-0 text-muted-foreground/70">—</span>
                  )}
                </div>
                {pdfUrl ? (
                  <iframe
                    src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
                    title={`PDF preview — ${label}`}
                    className="h-44 w-full rounded-b-md border-0 bg-white"
                    loading="lazy"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// "Links" column cell: the domestic (Ticketransfer) analogue of the QR column.
// International events deliver QR/PDF assets; domestic events deliver per-ticket
// Ticketransfer links. `linkAssets` is the sole-writer cache populated only for
// events with imported Ticketransfer tasks, so an empty array (every
// international listing) renders nothing and the column is invisible there.
// Coverage math (numerator/denominator computed over the same ticket set) lives
// in the shared link-coverage helper so the cell, its hover detail, and the CSV
// export never drift.
export type LinkCoverage = "full" | "partial" | "none";

export function getLinkCoverage(row: InventoryListing): LinkCoverage {
  const { withLink, denominator } = linkCoverageStats(row);
  if (withLink === 0) return "none";
  return withLink >= denominator ? "full" : "partial";
}

// Lazily-mounted hover body: only fetches the per-listing link URLs when the
// operator actually opens the card (Radix mounts HoverCardContent on open), so
// we never fan out a query per row. URLs are rendered as click-through anchors
// and are never logged.
function LinkCoverageDetails({
  row,
  denominator,
  withLink,
  qualifier,
}: {
  row: InventoryListing;
  denominator: number;
  withLink: number;
  qualifier: string;
}) {
  const assets = row.linkAssets ?? [];
  const { data: links, isLoading } = useListInventoryLinks(row.id);
  const urlByTicket = new Map<string, string>();
  const urlBySeat = new Map<string, string>();
  for (const l of links ?? []) {
    if (!l.url) continue;
    if (l.ticketId) urlByTicket.set(l.ticketId, l.url);
    if (l.seat) urlBySeat.set(l.seat, l.url);
  }
  const full = withLink >= denominator;
  return (
    <>
      <div className="px-3 py-2 border-b border-border/60">
        <p className="text-[11px] font-medium text-foreground">
          {full ? "Transfer links — full coverage" : "Transfer links — partial coverage"}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {withLink} of {denominator} {qualifier}ticket
          {denominator === 1 ? "" : "s"} have a transfer link
        </p>
      </div>
      <div className="max-h-80 overflow-y-auto p-2 space-y-1">
        {assets.map((a, i) => {
          const label = a.seat ? `Seat ${a.seat}` : `Ticket ${i + 1}`;
          const url =
            (a.ticketId ? urlByTicket.get(a.ticketId) : undefined) ??
            (a.seat ? urlBySeat.get(a.seat) : undefined) ??
            null;
          return (
            <div
              key={a.ticketId ?? `ticket-${i}`}
              className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-[11px]"
            >
              <span className="text-foreground truncate">
                {label}
                {a.sold ? (
                  <span className="text-muted-foreground"> (sold)</span>
                ) : null}
              </span>
              {a.hasLink ? (
                url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 font-medium text-sky-400 hover:underline"
                  >
                    Open ↗
                  </a>
                ) : isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                ) : (
                  <span className="shrink-0 font-medium text-sky-400">Link</span>
                )
              ) : (
                <span className="shrink-0 text-muted-foreground/70">—</span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export function LinkCoverageCell({ row }: { row: InventoryListing }) {
  const { withLink, denominator, qualifier } = linkCoverageStats(row);
  // No link assets at all = domestic event with no imported tasks yet, or an
  // international event. Render nothing so the column stays blank for events
  // where links are not applicable.
  if (denominator === 0) return null;
  // Has link assets but none matched — show a dim "needs link" indicator so
  // the column is never silently empty for a listing that is awaiting a
  // delivery link. This separates "ready to deliver" from "needs FVE action"
  // at a glance without the operator having to hover or count blanks.
  if (withLink === 0) {
    return (
      <HoverCard openDelay={150} closeDelay={120}>
        <HoverCardTrigger asChild>
          <span
            className="inline-flex items-center justify-center opacity-25"
            aria-label={`No transfer link — ${denominator} ${qualifier}ticket${denominator === 1 ? "" : "s"} need${denominator === 1 ? "s" : ""} one`}
          >
            <Link2 className="h-4 w-4" strokeWidth={1.5} />
          </span>
        </HoverCardTrigger>
        <HoverCardContent side="top" align="center" className="w-80 max-w-[20rem] p-0">
          <LinkCoverageDetails
            row={row}
            denominator={denominator}
            withLink={0}
            qualifier={qualifier}
          />
        </HoverCardContent>
      </HoverCard>
    );
  }
  const full = withLink >= denominator;
  return (
    <HoverCard openDelay={150} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex items-center justify-center"
          aria-label={
            full
              ? qualifier
                ? "All unsold tickets have a transfer link"
                : "All tickets have a transfer link"
              : `${withLink} of ${denominator} ${qualifier}tickets have a transfer link`
          }
        >
          <Link2
            className={cn(
              "h-4 w-4",
              full
                ? "text-sky-400"
                : "text-muted-foreground/60 [stroke-dasharray:2_2]",
            )}
            strokeWidth={full ? 2 : 1.5}
          />
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="top" align="center" className="w-80 max-w-[20rem] p-0">
        <LinkCoverageDetails
          row={row}
          denominator={denominator}
          withLink={withLink}
          qualifier={qualifier}
        />
      </HoverCardContent>
    </HoverCard>
  );
}

// "Listing" column cell: the Ticketmaster listing reference parsed from the
// confirming listing email, as a clickable monospace badge. Clicking opens the
// source listing email inline. Empty when the row has no listing reference.
export function ListingCell({ row }: { row: InventoryListing }) {
  const [open, setOpen] = useState(false);
  if (!row.listedListingRef) return null;
  const canOpen = row.listedEmailId != null;
  return (
    <>
      <button
        type="button"
        disabled={!canOpen}
        onClick={() => canOpen && setOpen(true)}
        className={cn(
          "rounded-sm border border-border bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] text-foreground",
          canOpen
            ? "cursor-pointer hover:bg-muted hover:brightness-110"
            : "cursor-default opacity-80",
        )}
        title={
          canOpen ? "View listing confirmation email" : row.listedListingRef
        }
      >
        {row.listedListingRef}
      </button>
      {canOpen && (
        <EmailViewSheet
          emailId={row.listedEmailId ?? null}
          open={open}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Tickets column: one colour-coded pip per ticket ──────────────────────────
// Replaces the old green "N sold ✓" Sales pills. Every ticket renders as a
// small pip; pips are grouped one line per listing (mirroring SeatsChainCell),
// so a purchase-grouped (buyback) row stacks a line per constituent listing.
// Hovering a line opens a portal HoverCard (safe inside virtualized rows) with
// the group's status breakdown and click-through to the matched sale email /
// the sale in Reach.
type TicketPipVariant = "unsold" | "sold" | "green" | "red" | "amber";

const TICKET_PIP_CLASS: Record<TicketPipVariant, string> = {
  // Unsold — hollow grey outline.
  unsold: "border border-zinc-500/70 bg-transparent",
  // Sold with no buy/sell intent — solid grey.
  sold: "border border-zinc-400 bg-zinc-400",
  // Sold on a Sell intent, or a confirmed buy-back — solid green.
  green: "border border-emerald-500 bg-emerald-500",
  // Failed buy-back / sold on FVE against a Buy-Back intent — solid red.
  red: "border border-red-500 bg-red-500",
  // Sold on a Buy-Back intent, re-purchase still pending — solid amber.
  amber: "border border-amber-500 bg-amber-500",
};

function TicketPip({ variant }: { variant: TicketPipVariant }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-3 w-[7px] shrink-0 rounded-[3px]",
        TICKET_PIP_CLASS[variant],
      )}
    />
  );
}

interface TicketGroupBreakdown {
  total: number;
  sold: number;
  unsold: number;
  soldVariant: Exclude<TicketPipVariant, "unsold">;
  soldLabel: string;
  unsoldLabel: string;
}

// Derive the per-listing ticket breakdown + pip colours from existing fields.
// total = ticketCount (fallback available + soldTicketCount); sold takes the
// larger of "total minus what's still available" and the confirmed sold-ticket
// count from matched sales, because Reach's availableQuantity lags on rows that
// have already sold (e.g. held rows keep availableQuantity at full even after a
// confirmed sale). unsold = the remainder.
export function ticketGroupBreakdown(
  listing: InventoryListing,
): TicketGroupBreakdown {
  const totalRaw = listing.ticketCount ?? null;
  const available = Math.max(0, listing.availableQuantity ?? 0);
  const soldFromMatches = Math.max(0, listing.soldTicketCount ?? 0);
  let total: number;
  let sold: number;
  if (totalRaw != null && totalRaw > 0) {
    total = totalRaw;
    const soldFromAvail = Math.max(0, total - Math.min(available, total));
    sold = Math.min(total, Math.max(soldFromMatches, soldFromAvail));
  } else {
    sold = soldFromMatches;
    total = available + sold;
  }
  const unsold = Math.max(0, total - sold);

  // buybackFveIntent drives the sold-pip colour; a sold buyback candidate with
  // no explicit intent yet is still treated as a buy-back (the same isBuyback
  // rule the app uses elsewhere).
  const isBuyback =
    listing.buybackFveIntent === "buy_back" ||
    (listing.buybackCandidate === true && listing.buybackFveIntent == null);
  const isSell = listing.buybackFveIntent === "sell";
  let soldVariant: Exclude<TicketPipVariant, "unsold">;
  let soldLabel: string;
  if (isBuyback) {
    if (
      listing.buybackBbConfirmed === true &&
      listing.buybackBbFailed !== true
    ) {
      soldVariant = "green";
      soldLabel = "bought back";
    } else if (listing.buybackBbFailed === true) {
      soldVariant = "red";
      soldLabel = "failed buy-back";
    } else {
      soldVariant = "amber";
      soldLabel = "sold · buy-back pending";
    }
  } else if (isSell) {
    soldVariant = "green";
    soldLabel = "sold";
  } else {
    soldVariant = "sold";
    soldLabel = "sold";
  }

  let unsoldLabel: string;
  if (listing.adminHoldActive) unsoldLabel = "held";
  else if (listing.listedOnFve)
    unsoldLabel = listing.reachBroadcast
      ? "listed · broadcasting"
      : "listed · not broadcast";
  else unsoldLabel = "available";

  return { total, sold, unsold, soldVariant, soldLabel, unsoldLabel };
}

// The hovercard body is a separate component so its matched-emails query only
// runs when the operator actually opens the card (Radix mounts HoverCardContent
// on open). Sale rows stay click-through to Reach / the sale email.
function TicketGroupHoverBody({
  listing,
  breakdown,
  onOpenEmail,
}: {
  listing: InventoryListing;
  breakdown: TicketGroupBreakdown;
  onOpenEmail: (emailId: number) => void;
}) {
  // Reach can record a sale with no matched TM email (common on buyback
  // events); reachSaleCount surfaces those so the query still runs.
  const hasSales =
    (listing.confirmedSaleCount ?? 0) > 0 || (listing.reachSaleCount ?? 0) > 0;
  const { data } = useListInventoryEmails(listing.id, undefined, {
    query: {
      enabled: hasSales,
      queryKey: getListInventoryEmailsQueryKey(listing.id, undefined),
    },
  });
  const sales = (data ?? []).filter((e) => emailIsSale(e.emailType));
  const seats = seatSummary(listing);
  return (
    <>
      <div className="border-b border-border/60 px-3 py-2">
        <p className="text-[11px] font-medium text-foreground">
          {breakdown.total} ticket{breakdown.total === 1 ? "" : "s"}
          {seats && seats !== "-" ? ` — ${seats}` : ""}
        </p>
        <div className="mt-1 space-y-0.5">
          {breakdown.sold > 0 && (
            <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <TicketPip variant={breakdown.soldVariant} />
              {breakdown.sold} {breakdown.soldLabel}
            </p>
          )}
          {breakdown.unsold > 0 && (
            <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <TicketPip variant="unsold" />
              {breakdown.unsold} unsold · {breakdown.unsoldLabel}
            </p>
          )}
        </div>
      </div>
      {sales.length > 0 && (
        <div className="max-h-64 space-y-1 overflow-y-auto p-2">
          {sales.map((e) => {
            const isDeAllocated = e.deAllocated === true;
            const label = e.quantity != null ? `${e.quantity} sold` : "sold";
            // Clicking a sale row opens the sale in Reach when a uniqueSaleId is
            // available; falls back to the email sheet for unreconciled rows.
            const handleClick = () => {
              if (e.reachSaleUrl) {
                window.open(e.reachSaleUrl, "_blank", "noopener,noreferrer");
              } else {
                onOpenEmail(e.emailId);
              }
            };
            return (
              <button
                key={e.emailId}
                type="button"
                onClick={handleClick}
                className="flex w-full items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-[11px] hover:bg-muted/60"
                aria-label={`Sale of ${e.quantity ?? "unknown"}. Click to ${
                  e.reachSaleUrl ? "open in Reach" : "view sale email"
                }.`}
              >
                <span className="truncate text-foreground">
                  {label}
                  {isDeAllocated ? (
                    <span className="text-muted-foreground">
                      {" "}
                      (de-allocated)
                    </span>
                  ) : e.reachExists ? (
                    <span className="text-emerald-400"> ✓</span>
                  ) : (
                    <span className="text-amber-300"> ?</span>
                  )}
                </span>
                <span className="shrink-0 font-medium text-emerald-400">
                  {e.reachSaleUrl ? "Open ↗" : "Email ↗"}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

// A single ticket-group line: the pips for one listing, wrapped in a hovercard.
// The EmailViewSheet lives outside the HoverCard so it survives the card
// closing when the pointer moves away after a click.
function TicketGroupLine({ listing }: { listing: InventoryListing }) {
  const [openEmailId, setOpenEmailId] = useState<number | null>(null);
  const breakdown = ticketGroupBreakdown(listing);
  if (breakdown.total <= 0) return null;
  const pips: ReactNode[] = [];
  for (let i = 0; i < breakdown.unsold; i++) {
    pips.push(<TicketPip key={`u${i}`} variant="unsold" />);
  }
  for (let i = 0; i < breakdown.sold; i++) {
    pips.push(<TicketPip key={`s${i}`} variant={breakdown.soldVariant} />);
  }
  const ariaLabel =
    `${breakdown.total} ticket${breakdown.total === 1 ? "" : "s"}` +
    (breakdown.sold > 0 ? `, ${breakdown.sold} ${breakdown.soldLabel}` : "") +
    (breakdown.unsold > 0
      ? `, ${breakdown.unsold} unsold · ${breakdown.unsoldLabel}`
      : "");
  return (
    <>
      <HoverCard openDelay={150} closeDelay={120}>
        <HoverCardTrigger asChild>
          <span
            className="inline-flex cursor-default flex-wrap items-center gap-[3px]"
            aria-label={ariaLabel}
          >
            {pips}
          </span>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="start"
          className="w-72 max-w-[18rem] p-0"
        >
          <TicketGroupHoverBody
            listing={listing}
            breakdown={breakdown}
            onOpenEmail={setOpenEmailId}
          />
        </HoverCardContent>
      </HoverCard>
      <EmailViewSheet
        emailId={openEmailId}
        open={openEmailId != null}
        onClose={() => setOpenEmailId(null)}
      />
    </>
  );
}

// Tickets column. Renders one pip line per listing. The flat / account / mobile
// views pass a single-element array; the purchase-grouped (buyback) view passes
// every constituent listing so each PO stacks a line per listing.
export function TicketsCell({ listings }: { listings: InventoryListing[] }) {
  const visible = listings.filter((l) => ticketGroupBreakdown(l).total > 0);
  if (visible.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      {visible.map((l) => (
        <TicketGroupLine key={l.id} listing={l} />
      ))}
    </div>
  );
}

// Per-row controls shared by both the By Event and By Account tables: the hold
// popover, the "open in Reach" link, and the activity sheet. Mutation wiring is
// supplied by the parent so invalidation can differ per view. The FVE status
// pill lives in its own dedicated column, not here.
export function RowActionsCell({
  row,
  defaultHoldDays,
  isBusy,
  onHold,
  onUnhold,
  fve,
}: {
  row: InventoryListing;
  defaultHoldDays: number;
  isBusy: boolean;
  onHold: (days: number) => void;
  onUnhold: () => void;
  fve?: FveTransitions;
}) {
  const reachId = row.reachListingId;
  const reachHref = reachId
    ? `https://tex.reachpro.com/inventory?uniqueInventoryId=${encodeURIComponent(reachId)}`
    : null;
  return (
    <div className="flex items-center justify-start gap-1.5 w-full">
      {isBusy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
      ) : (
        <>
          <HoldAction
            row={row}
            defaultHoldDays={defaultHoldDays}
            onHold={onHold}
            onUnhold={onUnhold}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              {reachHref ? (
                <a
                  href={reachHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                  aria-label="Open listing in Reach"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground/30 cursor-not-allowed">
                  <ExternalLink className="h-3.5 w-3.5" />
                </span>
              )}
            </TooltipTrigger>
            <TooltipContent>
              {reachHref ? "Open in Reach" : "No Reach listing"}
            </TooltipContent>
          </Tooltip>
          <ListingActivitySheet row={row} />
          <ListingEmailsCell row={row} />
        </>
      )}
    </div>
  );
}

export function ListingActivitySheet({ row }: { row: InventoryListing }) {
  const [open, setOpen] = useState(false);
  const params = { entityType: "inventory", entityId: String(row.id), limit: 50 };
  const { data, isLoading } = useListAuditLog(params, {
    query: { enabled: open, queryKey: getListAuditLogQueryKey(params) },
  });
  const label = [row.section, row.row].filter(Boolean).join(" · ") || `Listing #${row.id}`;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="View listing activity"
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          >
            <History className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Listing activity</TooltipContent>
      </Tooltip>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-sm">Listing Activity</SheetTitle>
          <SheetDescription className="text-xs">{label}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          <ActivityTimeline
            entries={data?.items ?? []}
            isLoading={isLoading}
            emptyText="No activity recorded for this listing yet."
          />
          <div className="pt-1">
            <Link href={`/activity?entityType=inventory&entityId=${row.id}`}>
              <span className="text-xs text-primary hover:underline cursor-pointer">
                View in full Activity →
              </span>
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function HoldAction({
  row,
  defaultHoldDays,
  onHold,
  onUnhold,
}: {
  row: InventoryListing;
  defaultHoldDays: number;
  onHold: (days: number) => void;
  onUnhold: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(defaultHoldDays);
  const [moreOpen, setMoreOpen] = useState(false);
  // Shared timeout so cursor travel from trigger → popover content doesn't
  // close the popover mid-flight. scheduleClose() queues a 160 ms close;
  // cancelClose() aborts it when either surface is re-entered.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleClose = () => {
    if (closeTimer.current != null) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  };
  const cancelClose = () => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  // Clear any pending close timer when this instance unmounts to avoid a
  // setState call on an unmounted component.
  useEffect(() => () => { if (closeTimer.current != null) clearTimeout(closeTimer.current); }, []);
  // On phones the fixed-width popover is cramped; render the same content in a
  // bottom sheet instead so the form gets full width without horizontal scroll.
  const isMobile = useIsMobile();

  useEffect(() => {
    setDays(defaultHoldDays);
  }, [defaultHoldDays]);

  // Reset the "More actions" disclosure each time the popover opens so it
  // always starts collapsed, mirroring the FVE status menu.
  useEffect(() => {
    if (open) setMoreOpen(false);
  }, [open]);

  if (!row.adminHoldActive) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-sm"
            onClick={() => onHold(defaultHoldDays)}
          >
            <Hand className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Place {defaultHoldDays}-day hold</TooltipContent>
      </Tooltip>
    );
  }

  const hs = holdState(row);
  const isExpiring = hs === "expiring";
  const stateMeta: Record<
    Exclude<HoldState, "none">,
    { label: string; className: string }
  > = {
    held: {
      label: "Held",
      className: "bg-amber-500/10 text-amber-500 border-amber-500/30",
    },
    expiring: {
      label: "Expiring",
      className: "bg-red-500/10 text-red-400 border-red-500/30",
    },
    expired: {
      label: "Expired",
      className: "bg-red-500/10 text-red-400 border-red-500/30",
    },
    failed: {
      label: "Verify failed",
      className: "bg-red-600/15 text-red-500 border-red-600/40",
    },
  };
  const meta = stateMeta[hs === "none" ? "held" : hs];
  const heldSince = formatHoldDate(row.adminHoldLastVerifiedAt);
  const expires = formatHoldDate(row.adminHoldExpiration);

  const triggerButton = (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 rounded-sm"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <Hand
        className={cn(
          "h-3.5 w-3.5",
          isExpiring
            ? "text-red-500 drop-shadow-[0_0_4px_rgba(239,68,68,0.7)]"
            : "text-amber-500 drop-shadow-[0_0_4px_rgba(245,158,11,0.6)]",
        )}
      />
    </Button>
  );

  const holdBody = (
    <div className="space-y-2.5">
          {/* Header: current hold state + when it expires */}
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Badge
                variant="outline"
                className={cn("text-[9px] uppercase", meta.className)}
              >
                {meta.label}
              </Badge>
              <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                <Hand className="h-3 w-3" /> hold
              </span>
            </div>
            {expires && (
              <div className="text-xs leading-snug text-foreground">
                <span className="text-muted-foreground">Expires </span>
                {expires}
              </div>
            )}
            {heldSince && (
              <div className="text-[11px] text-muted-foreground">
                Held since {heldSince}
              </div>
            )}
          </div>

          {/* Primary action: extend the hold */}
          <div className="space-y-1">
            <Button
              size="sm"
              className="h-8 w-full text-xs"
              onClick={() => {
                onHold(3);
                setOpen(false);
              }}
            >
              <Hand className="h-3.5 w-3.5 mr-1" /> Extend +3 days
            </Button>
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-destructive"
                onClick={() => {
                  onUnhold();
                  setOpen(false);
                }}
              >
                <Trash2 className="h-3 w-3" /> Release hold
              </button>
            </div>
          </div>

          {/* More actions: set a custom duration is tucked away */}
          <div className="border-t border-border pt-2">
            <button
              type="button"
              className="flex w-full items-center justify-between text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
            >
              <span>More actions</span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  moreOpen && "rotate-180",
                )}
              />
            </button>
            <div
              className={cn(
                "grid transition-all duration-200",
                moreOpen
                  ? "mt-2 grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="overflow-hidden">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-medium text-muted-foreground">
                    Custom duration
                  </label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min={1}
                      max={30}
                      value={days}
                      onChange={(e) =>
                        setDays(
                          Math.max(
                            1,
                            Math.min(30, Number(e.target.value) || 1),
                          ),
                        )
                      }
                      className="h-7 text-xs w-16"
                      aria-label="Hold duration in days"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      onClick={() => {
                        onHold(days);
                        setOpen(false);
                      }}
                    >
                      Set duration
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{triggerButton}</SheetTrigger>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-xl p-4"
        >
          <SheetTitle className="sr-only">Manage hold</SheetTitle>
          {holdBody}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-3"
        onMouseEnter={() => { cancelClose(); setOpen(true); }}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {holdBody}
      </PopoverContent>
    </Popover>
  );
}

// Freeform note cell shared by both tables. Empty rows show a muted icon; rows
// with a note show a truncated single-line preview. Both open the same popover
// with a multi-line editor. Saving is delegated to the parent (so invalidation
// can differ per view) and awaited so the popover stays disabled until it lands.
export function NoteCell({
  row,
  onSave,
}: {
  row: InventoryListing;
  onSave: (note: string | null) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(row.internalNotes ?? "");
  const [saving, setSaving] = useState(false);
  // On phones the fixed-width popover is cramped; render the same editor in a
  // bottom sheet instead so it gets full width without horizontal scroll.
  const isMobile = useIsMobile();

  // While the popover is open, pull the live "Custom Notes" tag straight from
  // Reach (the source of truth) so the editor reflects whatever is on file there.
  const {
    data: reachNote,
    isLoading: noteLoading,
    isError: noteError,
  } = useGetInventoryReachNote(row.id, {
    query: {
      enabled: open,
      queryKey: getGetInventoryReachNoteQueryKey(row.id),
      staleTime: 0,
      gcTime: 0,
    },
  });

  // Re-sync the textarea from a single source of truth while the popover is
  // open, so stale edits never linger. A single effect (rather than two that
  // both call setValue) ensures the local-DB value and the live Reach value can
  // never land competing updates in the same render cycle — the loop React
  // terminates with "too many re-renders" (#185). Once the Reach fetch settles
  // successfully it wins; until then (loading or error) we show the local value.
  useEffect(() => {
    if (!open) return;
    if (!noteLoading && !noteError && reachNote) {
      setValue(reachNote.note ?? "");
    } else {
      setValue(row.internalNotes ?? "");
    }
  }, [open, row.internalNotes, noteLoading, noteError, reachNote]);

  const noteLines = (row.internalNotes ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const handleSave = async () => {
    const trimmed = value.trim();
    setSaving(true);
    try {
      const result = await onSave(trimmed ? trimmed : null);
      // The note always saves locally; warn (without blocking) when the
      // best-effort mirror to Reach's "Custom Notes" tag didn't land so the
      // operator doesn't assume Reach is up to date when it isn't.
      const reachPush = (result as { reachPush?: { ok: boolean } | null })
        ?.reachPush;
      if (reachPush && !reachPush.ok) {
        toast.warning("Saved locally, but couldn't update Reach");
      }
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const triggerIcons =
    noteLines.length > 0 ? (
      <div className="flex gap-0.5 flex-wrap">
        {noteLines.map((line, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="rounded-sm text-amber-500 hover:text-amber-600 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onClick={() => setOpen(true)}
                aria-label={line}
              >
                <StickyNote className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{line}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    ) : (
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 rounded-sm text-muted-foreground/40 hover:text-foreground"
        aria-label="Add note"
        onClick={() => setOpen(true)}
      >
        <StickyNote className="h-3.5 w-3.5" />
      </Button>
    );

  const noteBody = (
    <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
            Note
          </div>
          <div className="relative">
            <Textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving || noteLoading}
              rows={4}
              placeholder={noteLoading ? "" : "Add a note…"}
              className="w-full text-xs resize-none"
            />
            {noteLoading && (
              <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading from Reach…
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              disabled={saving}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs min-w-[3.5rem]"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        {triggerIcons}
        <SheetContent
          side="bottom"
          className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-xl p-4"
        >
          <SheetTitle className="sr-only">Note</SheetTitle>
          {noteBody}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{triggerIcons}</PopoverAnchor>
      <PopoverContent align="start" className="w-64 p-3">
        {noteBody}
      </PopoverContent>
    </Popover>
  );
}

function fmtSetMeta(by?: string | null, at?: string | null): string | null {
  const when = formatHoldDateCompact(at);
  if (by && when) return `Set by ${by} · ${when}`;
  if (by) return `Set by ${by}`;
  if (when) return `Set ${when}`;
  return null;
}

// Single context-aware price cell. It collapses the old Face Value, Confirmed
// List and Rec. Price columns into one figure: the confirmed list price when a
// listing has one (email- or operator-set), otherwise an admin-set recommended
// price, otherwise the true face value. A small glyph shows where the figure
// came from, a ↓/↑ badge shows the gap to face value, and a rich tooltip lays
// out every underlying number. Admins keep the inline recommended-price editor;
// pullers keep the pending-acknowledge action — both reuse the parent-supplied
// mutations so invalidation can differ per view.
export function PriceCell({
  row,
  isAdmin,
  canAcknowledge = true,
  onSave,
  onAcknowledge,
}: {
  row: InventoryListing;
  isAdmin: boolean;
  /** Non-admins may only acknowledge their own listings (server-enforced). */
  canAcknowledge?: boolean;
  onSave: (price: number) => Promise<unknown>;
  onAcknowledge: () => Promise<unknown>;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [acking, setAcking] = useState(false);
  // On phones the fixed-width editor popover is cramped; render the same editor
  // in a bottom sheet instead so it gets full width without horizontal scroll.
  const isMobile = useIsMobile();

  const sym = currencySymbol(row.currencyCode);
  const eff = effectivePrice(row);
  const delta = priceVsFace(row);
  const face = row.faceValue;

  const recommended = row.recommendedListPrice;
  const recommendedIsSet = row.recommendedListPriceSetBy != null;
  const pending = !!row.recommendedChangePending;
  const ackedByEmail = !pending && row.recommendedAckSource === "email";
  // When a listing-confirmation email has arrived but the live price still
  // disagrees with the recommendation, the change stays pending. Surface the
  // gap so admins/pullers can see the listing was updated to the wrong price.
  const listed = row.listedPrice;
  const mismatch =
    pending &&
    recommended != null &&
    listed != null &&
    Math.abs(listed - recommended) > 0.005;
  const mismatchNote = mismatch
    ? `Listed at ${formatMoney(listed, row.currencyCode)} · Rec. ${formatMoney(recommended, row.currencyCode)}`
    : null;
  const setMeta = fmtSetMeta(
    row.recommendedListPriceSetBy,
    row.recommendedListPriceSetAt,
  );

  useEffect(() => {
    if (editOpen) setValue(recommended != null ? recommended.toFixed(2) : "");
  }, [editOpen, recommended]);

  const handleSave = async () => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return;
    setSaving(true);
    try {
      await onSave(num);
      setEditOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAck = async () => {
    setAcking(true);
    try {
      await onAcknowledge();
    } finally {
      setAcking(false);
    }
  };

  const SOURCE_TEXT: Record<
    EffectivePriceSource,
    { showing: string; why: string }
  > = {
    email: {
      showing: "confirmed list price",
      why: "Confirmed by a listing email.",
    },
    operator: {
      showing: "confirmed list price",
      why: "Set by an operator.",
    },
    recommended: {
      showing: "recommended price",
      why: "No confirmed list price yet.",
    },
    face: {
      showing: "face value",
      why: "No recommended or confirmed price is set.",
    },
    none: { showing: "no price", why: "No price information is available." },
  };

  const glyph =
    eff.source === "email" ? (
      <Mail
        className="h-3 w-3 text-muted-foreground/50 shrink-0"
        aria-label="Confirmed by listing email"
      />
    ) : eff.source === "operator" ? (
      <Pencil
        className="h-3 w-3 text-muted-foreground/50 shrink-0"
        aria-label="Set by operator"
      />
    ) : eff.source === "recommended" ? (
      <Sparkles
        className="h-3 w-3 text-muted-foreground/40 shrink-0"
        aria-label="Recommended price"
      />
    ) : null;

  const triggerInner = (
    <span className="inline-flex flex-col items-start gap-0.5 min-w-0">
      <span className="inline-flex items-center gap-1 min-w-0">
        <span
          className={cn(
            "font-mono text-xs tabular-nums",
            pending ? "text-amber-400" : "text-foreground",
          )}
        >
          {eff.value != null ? `${sym}${eff.value.toFixed(2)}` : "-"}
        </span>
        {glyph}
      </span>
      {delta !== 0 && (
        <span
          className={cn(
            "font-mono text-[9px] leading-none",
            delta < 0 ? "text-rose-400/80" : "text-emerald-400/70",
          )}
        >
          {delta < 0 ? "↓" : "↑"}
          {Math.abs(delta)}%
        </span>
      )}
    </span>
  );

  const tooltipBody = (
    <div className="px-3 py-2 text-[11px] leading-snug space-y-1.5">
      <div className="text-xs font-semibold">Price</div>
      <div className="opacity-90">
        Showing the {SOURCE_TEXT[eff.source].showing}.{" "}
        {SOURCE_TEXT[eff.source].why}
      </div>
      <div className="space-y-1 border-t border-border pt-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="opacity-70">Face value</span>
          <span className="font-mono">{formatMoney(face, row.currencyCode)}</span>
        </div>
        <div className="flex flex-col">
          <div className="flex items-baseline justify-between gap-3">
            <span className="opacity-70">Recommended</span>
            <span className="font-mono">
              {recommendedIsSet
                ? formatMoney(recommended, row.currencyCode)
                : `${formatMoney(face, row.currencyCode)} (system default)`}
            </span>
          </div>
          {recommendedIsSet && setMeta && (
            <span className="opacity-60 text-[10px]">{setMeta}</span>
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-baseline justify-between gap-3">
            <span className="opacity-70">Confirmed list</span>
            <span className="font-mono">
              {listed != null ? formatMoney(listed, row.currencyCode) : "—"}
            </span>
          </div>
          {listed != null && (
            <span className="opacity-60 text-[10px]">
              {row.listedSource === "email"
                ? `Listing email${
                    formatHoldDateCompact(row.listingConfirmedAt)
                      ? ` · ${formatHoldDateCompact(row.listingConfirmedAt)}`
                      : ""
                  }`
                : "Operator"}
            </span>
          )}
        </div>
      </div>
      {delta !== 0 && (
        <div className={delta < 0 ? "text-rose-300" : "text-emerald-300"}>
          {delta < 0
            ? `↓ ${Math.abs(delta)}% below face value`
            : `↑ ${delta}% above face value`}
        </div>
      )}
      {mismatchNote && <div className="text-rose-300">{mismatchNote}</div>}
      {pending && !mismatch && (
        <div className="text-amber-300">Pending puller acknowledgment.</div>
      )}
      <div className="opacity-60 pt-0.5">
        Open the row's activity for the full price history.
      </div>
    </div>
  );

  const editor = (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-foreground">
        Recommended list price
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xs text-muted-foreground">{sym}</span>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="h-7 text-xs flex-1"
          aria-label="Recommended list price"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
          }}
        />
      </div>
      <p className="text-[10px] text-muted-foreground">
        Saving notifies the puller via Slack and flags a pending change until
        they acknowledge it.
      </p>
      <div className="flex items-center justify-end gap-1.5">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          disabled={saving}
          onClick={() => setEditOpen(false)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs min-w-[3.5rem]"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );

  // Resolved by a listing-confirmation email rather than a manual click.
  const emailAckBadge = ackedByEmail ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0">
          <Mail className="h-3.5 w-3.5 text-emerald-400/80" />
        </span>
      </TooltipTrigger>
      <TooltipContent>Auto-confirmed by listing email</TooltipContent>
    </Tooltip>
  ) : null;

  return (
    <div className="flex items-center gap-1 w-full min-w-0">
      {isAdmin ? (
        isMobile ? (
          /* On phones the editor opens as a full-width bottom sheet. The hover
             tooltip is dropped here since the sheet carries the same info. */
          <Sheet open={editOpen} onOpenChange={setEditOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex text-left min-w-0 hover:opacity-80 transition-opacity"
                title="Edit recommended list price"
              >
                {triggerInner}
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-xl p-4"
            >
              <SheetTitle className="sr-only">
                Recommended list price
              </SheetTitle>
              {editor}
            </SheetContent>
          </Sheet>
        ) : (
          <Popover open={editOpen} onOpenChange={setEditOpen}>
            {/* Tooltip is forced closed while the editor popover is open so the
                two Radix layers never stack on top of each other. */}
            <Tooltip open={tipOpen && !editOpen} onOpenChange={setTipOpen}>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex text-left min-w-0 hover:opacity-80 transition-opacity"
                    title="Edit recommended list price"
                  >
                    {triggerInner}
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[280px] p-0">
                {tooltipBody}
              </TooltipContent>
            </Tooltip>
            <PopoverContent align="start" className="w-64 p-3">
              {editor}
            </PopoverContent>
          </Popover>
        )
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex min-w-0">{triggerInner}</span>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[280px] p-0">
            {tooltipBody}
          </TooltipContent>
        </Tooltip>
      )}

      {emailAckBadge}

      {pending &&
        (isAdmin ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0">
                <BellRing
                  className={cn(
                    "h-3.5 w-3.5",
                    mismatch ? "text-rose-400" : "text-amber-400",
                  )}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {mismatch
                ? `Listed price disagrees with recommendation — ${mismatchNote}`
                : "Awaiting puller acknowledgment"}
              {setMeta ? ` · ${setMeta}` : ""}
            </TooltipContent>
          </Tooltip>
        ) : canAcknowledge ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  "h-6 w-6 rounded-sm shrink-0",
                  mismatch
                    ? "text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                    : "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10",
                )}
                disabled={acking}
                onClick={handleAck}
                aria-label="Acknowledge recommended price change"
              >
                {acking ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {mismatch
                ? `Listed price disagrees with recommendation — ${mismatchNote}`
                : "New recommended price"}
              {setMeta ? ` · ${setMeta}` : ""} — click to acknowledge
            </TooltipContent>
          </Tooltip>
        ) : null)}
    </div>
  );
}

// ── Buyback FVE intent ────────────────────────────────────────────────────────
// In buyback-display mode (settings.hideGetIn) the FveStatusPill's Zone-3
// marketplace tiles are replaced with an explicit FVE intent picker
// (Buy Back / Sell). These shared colour tokens drive the active-tile state.
// ─────────────────────────────────────────────────────────────────────────────

type BuybackFveIntent = "buy_back" | "sell" | "cannot_list" | "surrender" | null;

const INTENT_COLORS: Record<NonNullable<BuybackFveIntent>, string> = {
  buy_back: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  sell: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cannot_list: "bg-red-500/15 text-red-400 border-red-500/30",
  surrender: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

// Cycle order for clicking through intent states in the INTENT column.
const INTENT_CYCLE: Record<string, BuybackFveIntent> = {
  null: "sell",
  sell: "buy_back",
  buy_back: "cannot_list",
  cannot_list: null,
};

const INTENT_LABEL: Record<string, string> = {
  sell: "Sell",
  buy_back: "Buy Back",
  cannot_list: "Cannot List",
  surrender: "Surrender",
};

/**
 * Standalone clickable intent icon for the INTENT column (buyback events only).
 * Clicking cycles through null → sell → buy_back → cannot_list → null and
 * immediately calls PATCH /inventory/:id/intent. Uses local optimistic state so
 * the icon updates before the server round-trip completes; reverts on error.
 */
export function IntentCell({
  row,
  fve,
}: {
  row: InventoryListing;
  fve: FveTransitions;
}) {
  const serverIntent = (row.buybackFveIntent ?? null) as BuybackFveIntent;

  const [localIntent, setLocalIntent] = useState<BuybackFveIntent>(serverIntent);
  const localIntentRef = useRef<BuybackFveIntent>(serverIntent);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmedIntentRef = useRef<BuybackFveIntent>(serverIntent);

  useEffect(() => {
    if (debounceRef.current === null) {
      setLocalIntent(serverIntent);
      localIntentRef.current = serverIntent;
      confirmedIntentRef.current = serverIntent;
    }
  }, [serverIntent]);

  const handleClick = () => {
    const key = localIntentRef.current ?? "null";
    const next = (INTENT_CYCLE[key] ?? null) as BuybackFveIntent;
    localIntentRef.current = next;
    setLocalIntent(next);

    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      const intent = localIntentRef.current;
      fve.setBuybackFveIntent(row.id, intent, {
        onError: () => {
          setLocalIntent(confirmedIntentRef.current);
          localIntentRef.current = confirmedIntentRef.current;
          toast.error("Failed to update intent");
        },
        onSuccess: () => {
          confirmedIntentRef.current = intent;
        },
      });
    }, 200);
  };

  const icon =
    localIntent === "sell" ? (
      <TrendingUp className="h-3.5 w-3.5" />
    ) : localIntent === "buy_back" ? (
      <RotateCcw className="h-3.5 w-3.5" />
    ) : localIntent === "cannot_list" ? (
      <Ban className="h-3.5 w-3.5" />
    ) : (
      <Minus className="h-3.5 w-3.5" />
    );

  const colorClass =
    localIntent === "sell"
      ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20"
      : localIntent === "buy_back"
        ? "text-amber-400 border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
        : localIntent === "cannot_list"
          ? "text-red-400 border-red-500/30 bg-red-500/10 hover:bg-red-500/20"
          : "text-muted-foreground/40 border-border/50 bg-transparent hover:bg-muted/30 hover:text-muted-foreground";

  const label = localIntent != null ? INTENT_LABEL[localIntent] : "Not set";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center justify-center rounded-full border w-6 h-6 transition-colors duration-150",
            colorClass,
          )}
          onClick={handleClick}
          aria-label={`Intent: ${label}`}
        >
          <span>
            {icon}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-xs font-semibold">Intent: {label}</div>
        <div className="text-[11px] opacity-70">Click to change</div>
      </TooltipContent>
    </Tooltip>
  );
}

// Renders the detected buyback purchase join (BB ACCT / BB PO). When the
// detection is ambiguous (multiple confident matches) the BB fields are blank;
// surface that as an "Ambiguous" badge so the operator opens the Status pill to
// pick the right purchase rather than seeing a misleadingly empty cell.
export function PoLink({ po }: { po?: string | null }) {
  if (!po) {
    return <span className="text-xs font-mono text-muted-foreground">-</span>;
  }
  return (
    <a
      href={`https://tex.reachpro.com/purchases?uniquePurchaseId=${encodeURIComponent(po)}`}
      target="_blank"
      rel="noreferrer"
      className="text-xs font-mono whitespace-nowrap truncate hover:underline text-foreground"
      title={po}
      onClick={(e) => e.stopPropagation()}
    >
      {po}
    </a>
  );
}

// ── Multi-account buy-back coverage breakdown ────────────────────────────────
// A split buy-back links one sold listing to 2+ buy-back accounts, each
// covering a subset of the original seats. The compact row can only show an
// aggregate "Multi (N)" pill, so this hover card reveals every linked account,
// the seats it covers, its per-link state, and any seats still uncovered.
// ─────────────────────────────────────────────────────────────────────────────

type BuybackCoverageView = NonNullable<InventoryListing["buybackCoverage"]>;

// Compress a seat list into a human range, e.g. ["05","06","07","08"] → "5-8",
// ["09","010"] → "9-10", ["3","5","6"] → "3, 5-6". Non-numeric seats are listed
// verbatim (leading zeros stripped) so nothing is silently dropped.
function formatSeatRange(seats: string[] | null | undefined): string {
  const cleaned = (seats ?? []).map((s) => String(s).trim()).filter(Boolean);
  if (cleaned.length === 0) return "—";
  const parsed = cleaned.map((s) => {
    const stripped = s.replace(/^0+(?=\d)/, "");
    const n = Number(stripped);
    return { raw: stripped, n: Number.isInteger(n) ? n : null };
  });
  if (parsed.some((p) => p.n == null)) {
    return parsed.map((p) => p.raw).join(", ");
  }
  const sorted = parsed.map((p) => p.n as number).sort((a, b) => a - b);
  const parts: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    if (cur === prev + 1) {
      prev = cur;
      continue;
    }
    parts.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = cur;
    prev = cur;
  }
  parts.push(start === prev ? `${start}` : `${start}-${prev}`);
  return parts.join(", ");
}

const BB_LINK_STATE_META: Record<
  string,
  { label: string; className: string }
> = {
  detected: {
    label: "Detected",
    className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  },
  confirmed: {
    label: "Confirmed",
    className: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  },
  pending: {
    label: "Pending",
    className: "bg-zinc-700/60 text-zinc-300 border-zinc-600/60",
  },
  failed: {
    label: "Failed",
    className: "bg-red-500/15 text-red-300 border-red-500/40",
  },
};

function BuybackCoverageBreakdown({
  coverage,
}: {
  coverage: BuybackCoverageView;
}) {
  const links = coverage.links ?? [];
  const covered = coverage.coveredCount ?? 0;
  const total = coverage.totalSeats ?? 0;
  const remaining = coverage.remainingSeats ?? [];
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-foreground">
          {links.length} buy-back accounts
        </span>
        <span className="text-muted-foreground whitespace-nowrap">
          {covered} of {total} seats covered
        </span>
      </div>
      <div className="space-y-1.5">
        {links.map((link) => {
          const meta = BB_LINK_STATE_META[link.state] ?? BB_LINK_STATE_META.pending;
          return (
            <div
              key={link.id}
              className="flex items-start justify-between gap-2 border-t border-border/40 pt-1.5 first:border-t-0 first:pt-0"
            >
              <div className="min-w-0 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-foreground truncate max-w-[13rem]">
                    {link.vendorEmail}
                  </span>
                  <CopyButton value={link.vendorEmail} />
                </div>
                <div className="text-muted-foreground">
                  <span>Seats {formatSeatRange(link.coveredSeats)}</span>
                  {link.purchaseOrderId ? (
                    <>
                      {" · PO "}
                      <a
                        href={`https://tex.reachpro.com/purchases?uniquePurchaseId=${encodeURIComponent(link.purchaseOrderId)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline text-foreground/80"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {link.purchaseOrderId}
                      </a>
                    </>
                  ) : null}
                </div>
              </div>
              <Badge
                variant="outline"
                className={cn("shrink-0 text-[9px] px-1 py-0", meta.className)}
              >
                {meta.label}
              </Badge>
            </div>
          );
        })}
      </div>
      {remaining.length > 0 && (
        <div className="text-amber-400 border-t border-border/40 pt-1.5">
          {remaining.length} seat{remaining.length === 1 ? "" : "s"} not yet
          covered: {formatSeatRange(remaining)}
        </div>
      )}
    </div>
  );
}

// Wraps a compact multi-link pill in a portalled hover card (safe inside
// react-virtual rows) that reveals the per-account breakdown. When there are
// fewer than 2 links there is nothing to expand, so the children render as-is.
function BuybackCoverageHoverCard({
  coverage,
  children,
}: {
  coverage: BuybackCoverageView | null | undefined;
  children: ReactNode;
}) {
  const links = coverage?.links ?? [];
  if (links.length < 2) return <>{children}</>;
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="inline-flex">{children}</div>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="w-auto max-w-[22rem] p-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <BuybackCoverageBreakdown coverage={coverage!} />
      </HoverCardContent>
    </HoverCard>
  );
}

// ── Seats › Sale › BB chain cell (purchase-flat view) ────────────────────────
// Per-purchase column that replaces both the old `seats` column and the
// `poChain` column in the purchase-grouped (buyback) view. Renders one line per
// constituent listing: [SeatPill] › [SALE pill] › [BB pill].
// ─────────────────────────────────────────────────────────────────────────────

function SeatsChainBbPill({
  listing,
  invalidate,
}: {
  listing: InventoryListing;
  invalidate: () => void;
}) {
  const bbFailed = listing.buybackBbFailed === true;
  const bbConfirmed = listing.buybackBbConfirmed === true;
  const bbDetected = listing.buybackBbPurchaseOrderId ?? null;
  const bbAmbiguous = listing.buybackMatchAmbiguous;
  const linkCount = listing.buybackLinks?.length ?? 0;
  const coverage = listing.buybackCoverage;

  // Hooks declared unconditionally before any early returns (Rules of Hooks)
  const [failOpen, setFailOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addSeats, setAddSeats] = useState("");
  const markFailed = useMarkBuybackFailed({
    mutation: {
      onSuccess: () => {
        setFailOpen(false);
        invalidate();
      },
    },
  });
  const confirmLink = useConfirmBuybackLink({
    mutation: { onSuccess: () => { invalidate(); } },
  });
  const failLink = useFailBuybackLink({
    mutation: { onSuccess: () => { invalidate(); } },
  });
  const addLink = useAddBuybackLink({
    mutation: {
      onSuccess: () => {
        setAddEmail("");
        setAddSeats("");
        invalidate();
      },
    },
  });

  const bbVendorEmail = listing.buybackBbVendorEmail?.trim() || null;

  const bbEmailTooltipContent = bbVendorEmail ? (
    <HoverCardContent
      side="top"
      align="start"
      className="w-auto p-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-mono text-foreground">{bbVendorEmail}</span>
        <CopyButton value={bbVendorEmail} />
      </div>
    </HoverCardContent>
  ) : null;

  // Shared popover body — renders the per-link list + add form + whole-listing fail.
  // Used by both the single-link PENDING pill and the multi-link aggregate pill.
  const anyLinkPending = confirmLink.isPending || failLink.isPending || addLink.isPending;
  const linksPopoverContent = (id: number, links: NonNullable<typeof listing.buybackLinks>) => (
    <PopoverContent
      side="top"
      align="start"
      className="w-72 p-3"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs font-medium mb-2">Buyback Accounts</p>
      {links.length > 0 ? (
        <div className="flex flex-col gap-1.5 mb-3 max-h-40 overflow-y-auto">
          {links.map((link) => {
            const stateMeta: Record<string, { label: string; cls: string }> = {
              pending: { label: "Pending", cls: "text-muted-foreground border-muted-foreground/30" },
              detected: { label: "Detected", cls: "text-amber-400 border-amber-400/40" },
              confirmed: { label: "Confirmed", cls: "text-green-400 border-green-400/40" },
              failed: { label: "Failed", cls: "text-red-400 border-red-400/40" },
            };
            const m = stateMeta[link.state] ?? { label: link.state, cls: "" };
            return (
              <div key={link.id} className="flex items-start gap-1 px-2 py-1.5 rounded bg-muted/40">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono truncate">{link.vendorEmail}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {link.coveredSeats.length > 0 ? link.coveredSeats.join(", ") : "No seats specified"}
                  </p>
                </div>
                <Badge variant="outline" className={cn("shrink-0 text-[9px] px-1 py-0 mr-0.5", m.cls)}>
                  {m.label}
                </Badge>
                <button
                  type="button"
                  title="Confirm this account bought back"
                  disabled={anyLinkPending}
                  onClick={() => confirmLink.mutate({ id, linkId: link.id })}
                  className="text-green-400 hover:text-green-300 disabled:opacity-40 shrink-0"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  title="Mark this account as failed"
                  disabled={anyLinkPending}
                  onClick={() => failLink.mutate({ id, linkId: link.id })}
                  className="text-red-400 hover:text-red-300 disabled:opacity-40 shrink-0"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mb-3">No accounts linked yet.</p>
      )}
      <div className="mb-3">
        <p className="text-xs font-medium text-muted-foreground mb-1">+ Add account</p>
        <Input
          type="email"
          className="h-7 text-xs mb-1.5"
          placeholder="account@example.com"
          value={addEmail}
          onChange={(e) => setAddEmail(e.target.value)}
        />
        <Input
          type="text"
          className="h-7 text-xs mb-1.5"
          placeholder="Seats e.g. 5,6,7 (optional)"
          value={addSeats}
          onChange={(e) => setAddSeats(e.target.value)}
        />
        <Button
          size="sm"
          className="w-full text-xs h-7"
          variant="outline"
          disabled={!addEmail.trim() || anyLinkPending}
          onClick={() =>
            addLink.mutate({
              id,
              data: {
                vendorEmail: addEmail.trim(),
                coveredSeats: addSeats.trim()
                  ? addSeats.split(",").map((s) => s.trim()).filter(Boolean)
                  : [],
              },
            })
          }
        >
          {addLink.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
          Add
        </Button>
      </div>
      <div className="border-t border-border pt-3">
        <p className="text-xs font-medium mb-1">Mark as Failed to Buy Back</p>
        <p className="text-xs text-muted-foreground mb-3">
          Confirm the re-purchase never happened.
        </p>
        <Button
          size="sm"
          variant="destructive"
          className="w-full text-xs h-7"
          disabled={markFailed.isPending || anyLinkPending}
          onClick={() => markFailed.mutate({ id })}
        >
          {markFailed.isPending && (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          )}
          Confirm — Mark Failed
        </Button>
      </div>
    </PopoverContent>
  );

  // Multi-link: when 2+ accounts are linked, show an actionable aggregate pill
  // that opens the same per-link management popover.
  if (linkCount > 1) {
    const coverageVariant: ChainPillVariant =
      coverage?.state === "complete"
        ? "green"
        : coverage?.state === "partial"
          ? "amber"
          : "dark";
    const coverageLabel =
      coverage?.state === "complete"
        ? `Multi (${linkCount}) — complete`
        : coverage?.state === "partial"
          ? `Multi (${linkCount}) — partial`
          : coverage?.state === "ambiguous"
            ? `Multi (${linkCount}) — ambiguous`
            : `Multi (${linkCount}) — pending`;
    return (
      <>
        {CHAIN_SEP}
        <Popover open={failOpen} onOpenChange={setFailOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex"
              onClick={(e) => e.stopPropagation()}
            >
              <ChainPill
                variant={coverageVariant}
                className="cursor-pointer hover:border-muted-foreground/60 transition-colors"
                title={`${linkCount} buy-back accounts — click to manage`}
              >
                <span className={coverage?.state === "pending" ? "opacity-50" : undefined}>
                  {coverageLabel}
                </span>
              </ChainPill>
            </button>
          </PopoverTrigger>
          {linksPopoverContent(listing.id, listing.buybackLinks ?? [])}
        </Popover>
      </>
    );
  }

  if (bbFailed) {
    return (
      <>
        {CHAIN_SEP}
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <span className="inline-flex">
              <ChainPill variant="red" title="Operator confirmed buy-back failed">
                FAILED
              </ChainPill>
            </span>
          </HoverCardTrigger>
          {bbEmailTooltipContent}
        </HoverCard>
      </>
    );
  }
  if (bbDetected) {
    return (
      <>
        {CHAIN_SEP}
        <HoverCard openDelay={300} closeDelay={100}>
          <HoverCardTrigger asChild>
            <span className="inline-flex">
              <ChainPill
                variant={bbAmbiguous ? "dark" : "green"}
                title={bbAmbiguous ? "Multiple matches — resolve in Status pill" : bbDetected}
              >
                {bbAmbiguous ? (
                  <span className="text-amber-300">Ambiguous</span>
                ) : (
                  <a
                    href={`https://tex.reachpro.com/purchases?uniquePurchaseId=${encodeURIComponent(bbDetected)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:underline truncate max-w-[80px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {bbDetected}
                  </a>
                )}
              </ChainPill>
            </span>
          </HoverCardTrigger>
          {bbEmailTooltipContent}
        </HoverCard>
      </>
    );
  }
  return (
    <>
      {CHAIN_SEP}
      <HoverCard openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className="inline-flex">
            <Popover open={failOpen} onOpenChange={setFailOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ChainPill
                    variant={bbConfirmed ? "blue" : "dark"}
                    className="cursor-pointer hover:border-muted-foreground/60 transition-colors"
                    title={
                      bbConfirmed
                        ? "Buy-back confirmed — awaiting Reach PO. Click to update."
                        : "BB re-purchase pending — click to confirm or mark failed"
                    }
                  >
                    <span className={bbConfirmed ? undefined : "opacity-50"}>PENDING</span>
                  </ChainPill>
                </button>
              </PopoverTrigger>
              {linksPopoverContent(listing.id, listing.buybackLinks ?? [])}
            </Popover>
          </span>
        </HoverCardTrigger>
        {bbEmailTooltipContent}
      </HoverCard>
    </>
  );
}

/**
 * Seats › Sale › BB chain for the flat purchase row. One line per constituent
 * listing — each line: [SeatPill] › [SALE pill] › [BB pill]. BB segment renders
 * only when the operator intent is "buy_back", or when no operator override exists
 * and the row was auto-detected as a buyback candidate. Explicit "sell" or
 * "cannot_list" intent always suppresses the BB pill even if `buybackCandidate`
 * is true. Row height grows naturally with the number of listings.
 */
export function SeatsChainCell({
  listings,
  invalidate,
}: {
  listings: InventoryListing[];
  invalidate: () => void;
}) {
  const [emailModalId, setEmailModalId] = useState<number | null>(null);
  const [salePickerRow, setSalePickerRow] = useState<InventoryListing | null>(null);

  return (
    <div className="flex flex-col gap-1 py-0.5 min-w-0 w-full">
      {listings.map((listing) => {
        const seat = seatSummary(listing);
        const firstSale = listing.saleOrderNumbers?.[0] ?? null;
        const extraSales = Math.max(0, (listing.saleOrderNumbers?.length ?? 0) - 1);
        // "Failed" wins over "confirmed": a failed buy-back never shows the blue
        // (sale-expected) SALE pill, even if a stale confirmed flag lingers.
        const bbDetected = listing.buybackBbPurchaseOrderId ?? null;
        const bbConfirmed =
          listing.buybackBbConfirmed === true && listing.buybackBbFailed !== true;
        const isBuyback =
          listing.buybackFveIntent === "buy_back" ||
          (listing.buybackCandidate === true && listing.buybackFveIntent == null);
        // Spec: blue = sell (non-buyback), green = buyback
        const salePillVariant: ChainPillVariant = firstSale
          ? isBuyback
            ? "green"
            : "blue"
          : "dark";
        const saleEmailId = listing.fveStatusEmailId ?? null;

        const salePill = (
          <ChainPill variant={salePillVariant} title={firstSale ?? "No sale yet"}>
            {firstSale ? (
              <span className="flex items-center gap-0.5">
                <span className="truncate max-w-[80px]">{firstSale}</span>
                {extraSales > 0 && (
                  <span className="text-[9px] opacity-70">+{extraSales}</span>
                )}
              </span>
            ) : (
              <span className="opacity-50">PENDING</span>
            )}
          </ChainPill>
        );

        return (
          <div key={listing.id} className="flex items-center gap-0.5 flex-wrap min-w-0">
            {seat && seat !== "-" ? (
              <SeatPill seat={seat} />
            ) : (
              <span className="text-muted-foreground/40 text-[10px] font-mono">—</span>
            )}
            {CHAIN_SEP}
            {firstSale && saleEmailId != null ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEmailModalId(saleEmailId);
                    }}
                  >
                    {salePill}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Click to view sale email</TooltipContent>
              </Tooltip>
            ) : !firstSale ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSalePickerRow(listing);
                    }}
                  >
                    <ChainPill
                      variant={bbDetected ? "amber" : bbConfirmed ? "blue" : "dark"}
                      className="cursor-pointer hover:border-muted-foreground/60 transition-colors"
                      title={
                        bbDetected
                          ? "Buy-back purchase detected — click to link a sale email"
                          : bbConfirmed
                            ? "Buy-back confirmed — click to link a sale email"
                            : "No sale matched — click to link a sale email"
                      }
                    >
                      <span className={bbDetected || bbConfirmed ? undefined : "opacity-50"}>PENDING</span>
                    </ChainPill>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {bbDetected ? "Buy-back purchase detected — waiting for sale email" : "Click to link a sale email"}
                </TooltipContent>
              </Tooltip>
            ) : (
              salePill
            )}
            {isBuyback && (
              <SeatsChainBbPill listing={listing} invalidate={invalidate} />
            )}
          </div>
        );
      })}
      <EmailViewerModal
        emailId={emailModalId}
        onClose={() => setEmailModalId(null)}
      />
      {salePickerRow && (
        <SalePickerModal
          row={salePickerRow}
          open={salePickerRow != null}
          onClose={() => setSalePickerRow(null)}
          invalidate={invalidate}
        />
      )}
    </div>
  );
}

export function BuybackJoinCell({
  row,
  field,
}: {
  row: InventoryListing;
  field: "acct" | "po";
}) {
  // Multi-link: when more than one buy-back link exists, aggregate display.
  const linkCount = row.buybackLinks?.length ?? 0;
  if (linkCount > 1) {
    const coverage = row.buybackCoverage;
    const coverageLabel =
      coverage?.state === "complete"
        ? "complete"
        : coverage?.state === "partial"
          ? "partial"
          : coverage?.state === "ambiguous"
            ? "ambiguous"
            : null;
    const coverageBadgeClass =
      coverage?.state === "complete"
        ? "bg-emerald-500/10 text-emerald-400 border-emerald-400/30"
        : coverage?.state === "partial"
          ? "bg-amber-500/10 text-amber-500 border-amber-500/30"
          : coverage?.state === "ambiguous"
            ? "bg-red-500/10 text-red-400 border-red-400/30"
            : "bg-zinc-500/10 text-zinc-400 border-zinc-400/30";
    return (
      <BuybackCoverageHoverCard coverage={coverage}>
        <div className="flex items-center gap-1 min-w-0">
          <Badge
            variant="outline"
            className={`shrink-0 text-[9px] px-1 py-0 ${coverageBadgeClass}`}
            title={`${linkCount} buy-back accounts linked`}
          >
            Multiple ({linkCount})
          </Badge>
          {coverageLabel && (
            <Badge
              variant="outline"
              className={`shrink-0 text-[8px] px-1 py-0 ${coverageBadgeClass}`}
            >
              {coverageLabel}
            </Badge>
          )}
        </div>
      </BuybackCoverageHoverCard>
    );
  }

  if (row.buybackMatchAmbiguous) {
    return (
      <Badge
        variant="outline"
        className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-[9px] uppercase"
        title="Multiple buyback purchases match — choose one in the Status pill"
      >
        Ambiguous
      </Badge>
    );
  }
  const val =
    field === "acct" ? row.buybackBbVendorEmail : row.buybackBbPurchaseOrderId;
  // buybackMatchScope='seat_group' = exact seat-set match confirmed.
  const isExact = row.buybackMatchScope === "seat_group";
  if (field === "po") {
    return (
      <div className="flex items-center gap-1 min-w-0">
        <PoLink po={val} />
        {val && isExact && (
          <Badge
            variant="outline"
            className="shrink-0 text-[8px] px-1 py-0 bg-emerald-500/10 text-emerald-400 border-emerald-400/30"
            title="Exact seat-set match"
          >
            exact
          </Badge>
        )}
      </div>
    );
  }
  return (
    <div
      className="text-xs whitespace-nowrap truncate"
      title={val ?? ""}
    >
      {val || "-"}
    </div>
  );
}

export function BuybackMarginCell({
  row,
  onConfirmSell,
  invalidate,
}: {
  row: InventoryListing;
  /** Called when the operator clicks "Confirm SELL" — should fire markListed. */
  onConfirmSell: () => void;
  /** Invalidates the event inventory query after a get-in update. */
  invalidate: () => void;
}) {
  const [getInValue, setGetInValue] = useState(
    row.getIn != null ? row.getIn.toFixed(2) : "",
  );
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const setGetIn = useSetBuybackGetIn({ mutation: { onSuccess: invalidate } });

  const sym = currencySymbol(row.currencyCode);
  const unitCost = row.unitCost;
  const marginPct = row.marginPct;
  const intent = row.recommendedIntent;
  const confirmed = row.buybackIntent === "SELL";
  const rowStatus = (row.fveStatus ?? "") as string;
  const isListed = rowStatus === "listed" || rowStatus === "listed_discount";
  const isSold = rowStatus === "sold" || rowStatus === "sold_partial";

  const marginColor =
    intent === "SELL"
      ? "text-emerald-400"
      : intent === "BUYBACK"
        ? "text-amber-400"
        : "text-muted-foreground";

  const handleSave = async () => {
    const num = Number(getInValue);
    if (!Number.isFinite(num) || num < 0) return;
    setSaving(true);
    try {
      await setGetIn.mutateAsync({ id: row.id, data: { getIn: num } });
      setEditing(false);
    } catch {
      toast.error("Failed to save get-in price");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {/* Unit cost reference */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-[11px] font-mono text-muted-foreground shrink-0 cursor-default">
            {unitCost != null ? `${sym}${unitCost.toFixed(2)}` : "—"}
          </span>
        </TooltipTrigger>
        <TooltipContent>Unit cost</TooltipContent>
      </Tooltip>

      <span className="text-muted-foreground/40 text-[10px]">→</span>

      {/* Editable get-in price */}
      {editing ? (
        <div className="flex items-center gap-1 shrink-0">
          <Input
            className="h-5 w-16 px-1 py-0 text-[11px] font-mono"
            value={getInValue}
            autoFocus
            onChange={(e) => setGetInValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setEditing(false);
            }}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5 text-emerald-400 hover:text-emerald-300"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3" />
            )}
          </Button>
        </div>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => {
                setGetInValue(row.getIn != null ? row.getIn.toFixed(2) : "");
                setEditing(true);
              }}
              className="text-[11px] font-mono text-foreground hover:text-primary hover:underline shrink-0 cursor-pointer"
            >
              {row.getIn != null ? `${sym}${row.getIn.toFixed(2)}` : (
                <span className="text-muted-foreground/50 italic text-[10px]">set get-in</span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>Get-in (resale) price — click to edit</TooltipContent>
        </Tooltip>
      )}

      {/* Margin % with intent color */}
      {marginPct != null && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn("text-[10px] font-mono shrink-0 cursor-default", marginColor)}>
              {marginPct >= 0 ? "+" : ""}{(marginPct * 100).toFixed(0)}%
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {intent === "SELL"
              ? "Margin above threshold — SELL recommended"
              : intent === "BUYBACK"
                ? "Margin below threshold — consider BUYBACK"
                : "Set get-in price to compute margin"}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Recommended intent badge */}
      {intent != null && !confirmed && (
        <span
          className={cn(
            "shrink-0 rounded px-1 py-0 text-[9px] font-semibold uppercase tracking-wide leading-4",
            intent === "SELL"
              ? "bg-emerald-500/15 text-emerald-400"
              : "bg-amber-500/15 text-amber-400",
          )}
        >
          {intent}
        </span>
      )}

      {/* Confirm SELL button — shown for any undecided buyback row not yet listed/sold */}
      {!confirmed && !isListed && !isSold && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 shrink-0 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
              onClick={onConfirmSell}
              aria-label="Confirm SELL"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {intent === "SELL"
              ? "Confirm SELL — marks this listing on FVE"
              : "Override to SELL — marks this listing on FVE despite low margin"}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Already confirmed badge */}
      {confirmed && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0 rounded px-1 py-0 text-[9px] font-semibold uppercase tracking-wide leading-4 bg-emerald-500/15 text-emerald-400/70">
              SELL ✓
            </span>
          </TooltipTrigger>
          <TooltipContent>SELL confirmed</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// PO › SALE › BB chain column
// ──────────────────────────────────────────────────────────────────────────────

export type ChainPillVariant = "dark" | "blue" | "green" | "red" | "amber";

const CHAIN_PILL_CLASS: Record<ChainPillVariant, string> = {
  dark: "bg-zinc-800/70 text-zinc-300 border-zinc-700/60",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  red: "bg-red-500/15 text-red-300 border-red-500/40",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/40",
};

export function ChainPill({
  children,
  variant = "dark",
  className,
  title,
}: {
  children: ReactNode;
  variant?: ChainPillVariant;
  className?: string;
  title?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] leading-none border whitespace-nowrap font-mono",
        CHAIN_PILL_CLASS[variant],
        className,
      )}
      title={title}
    >
      {children}
    </span>
  );
}

export const CHAIN_SEP = (
  <span className="text-muted-foreground/50 text-[10px] leading-none shrink-0 select-none">
    ›
  </span>
);

/**
 * Combined PO › SALE › BB pill-chain column. Replaces the separate Orig PO and
 * BB PO columns. For buyback-candidate rows (sold + awaiting re-purchase) all
 * three slots render; for non-buyback / sell rows only ORIG PO › SALE render.
 *
 * The PENDING BB pill opens a portal Popover (safe inside react-virtual rows)
 * with a "Mark as Failed to Buy Back" action that sets buybackBbFailed = true.
 */
export function PoChainCell({
  row,
  invalidate,
}: {
  row: InventoryListing;
  /** Invalidates the event inventory query after a mutation. */
  invalidate: () => void;
}) {
  const isBuyback =
    row.buybackFveIntent === "buy_back" ||
    (row.buybackCandidate === true && row.buybackFveIntent == null);
  const firstSale = row.saleOrderNumbers?.[0] ?? null;
  const extraSales = Math.max(0, (row.saleOrderNumbers?.length ?? 0) - 1);

  // SALE pill: blue for buyback rows, green for non-buyback (sell) rows.
  const salePillVariant: ChainPillVariant = firstSale
    ? isBuyback
      ? "blue"
      : "green"
    : "dark";

  const bbDetected = row.buybackBbPurchaseOrderId ?? null;
  const bbFailed = row.buybackBbFailed === true;
  // "Failed" wins over "confirmed": a failed buy-back never shows the blue
  // (sale-expected) SALE pill, even if a stale confirmed flag lingers.
  const bbConfirmed = row.buybackBbConfirmed === true && !bbFailed;
  const bbAmbiguous = row.buybackMatchAmbiguous;

  const bbVendorEmail = row.buybackBbVendorEmail?.trim() || null;

  const [failOpen, setFailOpen] = useState(false);
  const [salePickerOpen, setSalePickerOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addSeats, setAddSeats] = useState("");
  const markFailed = useMarkBuybackFailed({
    mutation: {
      onSuccess: () => {
        setFailOpen(false);
        invalidate();
      },
    },
  });
  const confirmLink = useConfirmBuybackLink({
    mutation: { onSuccess: () => { invalidate(); } },
  });
  const failLink = useFailBuybackLink({
    mutation: { onSuccess: () => { invalidate(); } },
  });
  const addLink = useAddBuybackLink({
    mutation: {
      onSuccess: () => {
        setAddEmail("");
        setAddSeats("");
        invalidate();
      },
    },
  });

  return (
    <>
    <div className="flex items-center gap-0.5 flex-wrap min-w-0">
      {/* ── ORIG PO pill ── */}
      <ChainPill variant="dark" title={row.purchaseOrderId ?? undefined}>
        {row.purchaseOrderId ? (
          <a
            href={`https://tex.reachpro.com/purchases?uniquePurchaseId=${encodeURIComponent(row.purchaseOrderId)}`}
            target="_blank"
            rel="noreferrer"
            className="hover:underline truncate max-w-[80px]"
            onClick={(e) => e.stopPropagation()}
          >
            {row.purchaseOrderId}
          </a>
        ) : (
          <span className="text-zinc-500">—</span>
        )}
      </ChainPill>

      {CHAIN_SEP}

      {/* ── SALE pill ── */}
      {firstSale ? (
        <ChainPill variant={salePillVariant} title={firstSale}>
          <span className="flex items-center gap-0.5">
            <span className="truncate max-w-[80px]">{firstSale}</span>
            {extraSales > 0 && (
              <span className="text-[9px] opacity-70">+{extraSales}</span>
            )}
          </span>
        </ChainPill>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex"
              onClick={(e) => {
                e.stopPropagation();
                setSalePickerOpen(true);
              }}
            >
              <ChainPill
                variant={bbDetected ? "amber" : bbConfirmed ? "blue" : "dark"}
                className="cursor-pointer hover:border-muted-foreground/60 transition-colors"
                title={
                  bbDetected
                    ? "Buy-back purchase detected — click to link a sale email"
                    : bbConfirmed
                      ? "Buy-back confirmed — click to link a sale email"
                      : "No sale matched — click to link a sale email"
                }
              >
                <span className={bbDetected || bbConfirmed ? undefined : "opacity-50"}>PENDING</span>
              </ChainPill>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {bbDetected ? "Buy-back purchase detected — waiting for sale email" : "Click to link a sale email"}
          </TooltipContent>
        </Tooltip>
      )}

      {/* ── BB pill (buyback rows only) ── */}
      {isBuyback && (
        <>
          {CHAIN_SEP}
          {/* BB pill: failed / detected are terminal; all pending cases (0/1/many links)
              open the same actionable per-link management popover. */}
          {bbFailed ? (
            <ChainPill variant="red" title="Operator confirmed buy-back failed">
              FAILED
            </ChainPill>
          ) : bbDetected ? (
            <ChainPill
              variant={bbAmbiguous ? "dark" : "green"}
              title={bbAmbiguous ? "Multiple matches — resolve in Status pill" : bbDetected}
            >
              {bbAmbiguous ? (
                <span className="text-amber-300">Ambiguous</span>
              ) : (
                <a
                  href={`https://tex.reachpro.com/purchases?uniquePurchaseId=${encodeURIComponent(bbDetected)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline truncate max-w-[80px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  {bbDetected}
                </a>
              )}
            </ChainPill>
          ) : (
            // Pending state: pill adapts to link count (PENDING for 0/1, Multi (N) for 2+).
            // Both open the same per-link management popover.
            // Radix Popover portals by default, safe inside react-virtual rows.
            <Popover open={failOpen} onOpenChange={setFailOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex"
                  onClick={(e) => e.stopPropagation()}
                >
                  {(row.buybackLinks?.length ?? 0) > 1 ? (
                    (() => {
                      const lc = row.buybackLinks!.length;
                      const cov = row.buybackCoverage;
                      const v: ChainPillVariant =
                        cov?.state === "complete" ? "green" : cov?.state === "partial" ? "amber" : "dark";
                      const lbl =
                        cov?.state === "complete"
                          ? `Multi (${lc}) ✓`
                          : cov?.state === "partial"
                            ? `Multi (${lc}) partial`
                            : cov?.state === "ambiguous"
                              ? `Multi (${lc}) ambiguous`
                              : `Multi (${lc}) pending`;
                      return (
                        <ChainPill
                          variant={v}
                          className="cursor-pointer hover:border-muted-foreground/60 transition-colors"
                          title={`${lc} buy-back accounts — click to manage`}
                        >
                          <span className={cov?.state === "pending" ? "opacity-50" : undefined}>{lbl}</span>
                        </ChainPill>
                      );
                    })()
                  ) : (
                    <ChainPill
                      variant={bbConfirmed ? "blue" : "dark"}
                      className="cursor-pointer hover:border-muted-foreground/60 transition-colors"
                      title={
                        bbConfirmed
                          ? "Buy-back confirmed — awaiting Reach PO. Click to update."
                          : "BB re-purchase pending — click to confirm or mark failed"
                      }
                    >
                      <span className={bbConfirmed ? undefined : "opacity-50"}>PENDING</span>
                    </ChainPill>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                className="w-72 p-3"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-xs font-medium mb-2">Buyback Accounts</p>
                {(row.buybackLinks?.length ?? 0) > 0 ? (
                  <div className="flex flex-col gap-1.5 mb-3 max-h-40 overflow-y-auto">
                    {row.buybackLinks!.map((link) => {
                      const stateMeta: Record<string, { label: string; cls: string }> = {
                        pending: { label: "Pending", cls: "text-muted-foreground border-muted-foreground/30" },
                        detected: { label: "Detected", cls: "text-amber-400 border-amber-400/40" },
                        confirmed: { label: "Confirmed", cls: "text-green-400 border-green-400/40" },
                        failed: { label: "Failed", cls: "text-red-400 border-red-400/40" },
                      };
                      const m = stateMeta[link.state] ?? { label: link.state, cls: "" };
                      const anyPending = confirmLink.isPending || failLink.isPending || addLink.isPending;
                      return (
                        <div key={link.id} className="flex items-start gap-1 px-2 py-1.5 rounded bg-muted/40">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-mono truncate">{link.vendorEmail}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {link.coveredSeats.length > 0 ? link.coveredSeats.join(", ") : "No seats specified"}
                            </p>
                          </div>
                          <Badge variant="outline" className={cn("shrink-0 text-[9px] px-1 py-0 mr-0.5", m.cls)}>
                            {m.label}
                          </Badge>
                          <button
                            type="button"
                            title="Confirm this account bought back"
                            disabled={anyPending}
                            onClick={() => confirmLink.mutate({ id: row.id, linkId: link.id })}
                            className="text-green-400 hover:text-green-300 disabled:opacity-40 shrink-0"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Mark this account as failed"
                            disabled={anyPending}
                            onClick={() => failLink.mutate({ id: row.id, linkId: link.id })}
                            className="text-red-400 hover:text-red-300 disabled:opacity-40 shrink-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mb-3">No accounts linked yet.</p>
                )}
                <div className="mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">+ Add account</p>
                  <Input
                    type="email"
                    className="h-7 text-xs mb-1.5"
                    placeholder="account@example.com"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                  />
                  <Input
                    type="text"
                    className="h-7 text-xs mb-1.5"
                    placeholder="Seats e.g. 5,6,7 (optional)"
                    value={addSeats}
                    onChange={(e) => setAddSeats(e.target.value)}
                  />
                  <Button
                    size="sm"
                    className="w-full text-xs h-7"
                    variant="outline"
                    disabled={!addEmail.trim() || addLink.isPending || confirmLink.isPending || failLink.isPending}
                    onClick={() =>
                      addLink.mutate({
                        id: row.id,
                        data: {
                          vendorEmail: addEmail.trim(),
                          coveredSeats: addSeats.trim()
                            ? addSeats.split(",").map((s) => s.trim()).filter(Boolean)
                            : [],
                        },
                      })
                    }
                  >
                    {addLink.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    Add
                  </Button>
                </div>
                <div className="border-t border-border pt-3">
                  <p className="text-xs font-medium mb-1">Mark as Failed to Buy Back</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Confirm the re-purchase never happened.
                  </p>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="w-full text-xs h-7"
                    disabled={markFailed.isPending || confirmLink.isPending || failLink.isPending || addLink.isPending}
                    onClick={() => markFailed.mutate({ id: row.id })}
                  >
                    {markFailed.isPending && (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    )}
                    Confirm — Mark Failed
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {bbVendorEmail && (
            <span
              className="text-[9px] text-muted-foreground truncate max-w-[120px]"
              title={`Buyback account: ${bbVendorEmail}`}
            >
              {bbVendorEmail}
            </span>
          )}
        </>
      )}
    </div>
    {salePickerOpen && (
      <SalePickerModal
        row={row}
        open={salePickerOpen}
        onClose={() => setSalePickerOpen(false)}
        invalidate={invalidate}
      />
    )}
    </>
  );
}
